import React, { useState, useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Spin, Checkbox, Tag, message, Button, InputNumber, Select, Divider,
  Table, Tooltip, Segmented, Slider, Switch,
} from 'antd';
import {
  ExperimentOutlined,
  BarChartOutlined, LineChartOutlined, DotChartOutlined,
  ToolOutlined, FundOutlined, ClusterOutlined, FieldTimeOutlined,
  HeatMapOutlined, SafetyCertificateOutlined, SwapOutlined, PieChartOutlined,
  FileTextOutlined, ReloadOutlined, HighlightOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import ReactEChartsCore from 'echarts-for-react';
import 'echarts-gl';
import FileUpload from '../components/FileUpload';
import AIPanel from '../components/AIPanel';
import PrerequisiteCard from '../components/PrerequisiteCard';
import VerdictCard from '../components/VerdictCard';
import SOPAdvice from '../components/SOPAdvice';
import TopStats from '../components/TopStats';
import StatTable from '../components/StatTable';
import ConfidenceIntervalChart from '../components/ConfidenceIntervalChart';
import {
  API_BASE_URL, getPreview, getColumns, getTopology, runAnalysis, fetchTestData,
} from '../services/api';
import type { ColumnInfo, PreviewData, RecommendedModule, AIExplanation } from '../types';
import { injectAxisRange, injectDarkMode } from '../utils/chartUtils';
// HypothesisPanel removed — replaced by unified ModuleOverlay
import {
  AnnotationCanvas,
  AnnotationToolbar,
  annotationReducer,
  initialAnnotationStore,
} from '../annotations';

// DOE metadata column names to hide from factor/response selection
// Must match backend doe.py _META_PATTERNS (normalized: lowercase, no spaces/underscores)
const DOE_META_NAMES = new Set([
  'stdorder', 'runorder', 'standardorder', 'centerpt', 'centerpoint',
  'blocks', 'block', '标准序', '运行序', '区组', '中心点', '点类型',
]);

const MISSING_STRATEGIES = [
  { value: 'none', label: '不处理' },
  { value: 'drop', label: '删除缺失行' },
  { value: 'mean', label: '均值填充' },
  { value: 'median', label: '中位数填充' },
];

const ALL_MODULES = [
  { key: 'basic_stats', name: '基本统计', icon: <BarChartOutlined />, color: '#533afd' },
  { key: 'process_capability', name: '过程能力', icon: <FundOutlined />, color: '#533afd' },
  { key: 'spc', name: 'SPC控制图', icon: <LineChartOutlined />, color: '#0073e6' },
  { key: 'hypothesis', name: '假设检验', icon: <ExperimentOutlined />, color: '#df1b41' },
  { key: 'msa', name: '测量系统分析', icon: <ToolOutlined />, color: '#0073e6' },
  { key: 'regression', name: '回归分析', icon: <DotChartOutlined />, color: '#15be53' },
  { key: 'doe', name: 'DOE实验设计', icon: <ClusterOutlined />, color: '#8b5cf6' },
  { key: 'time_series', name: '时间序列', icon: <FieldTimeOutlined />, color: '#e6a200' },
  { key: 'multivariate', name: '多变量分析', icon: <HeatMapOutlined />, color: '#06b6d4' },
  { key: 'reliability', name: '可靠性分析', icon: <SafetyCertificateOutlined />, color: '#8b5cf6' },
  { key: 'nonparametric', name: '非参数分析', icon: <SwapOutlined />, color: '#64748d' },
  { key: 'chi_square', name: '卡方分析', icon: <PieChartOutlined />, color: '#15be53' },
];

const TYPE_TAG_COLORS: Record<string, string> = {
  float64: 'blue', int64: 'cyan', float: 'blue', integer: 'cyan',
  object: 'green', categorical: 'green', datetime: 'orange',
};

// ---- DOE Interactive Factor Explorer ----
const DoeFactorExplorer: React.FC<{ predictionInfo: any; isDark: boolean }> = ({ predictionInfo, isDark }) => {
  const factors: Array<{ name: string; levels: number[]; min: number; max: number; effect: Record<string, number> }> = predictionInfo.factors || [];
  const grandMean: number = predictionInfo.grand_mean || 0;

  // Initialize slider values to midpoint of each factor's range
  const [sliderValues, setSliderValues] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const f of factors) {
      init[f.name] = (f.min + f.max) / 2;
    }
    return init;
  });

  // Predict Y from current slider positions
  const predictedY = useMemo(() => {
    if (predictionInfo.equation_type === 'rsm' && predictionInfo.model_params) {
      // RSM polynomial prediction: Y = intercept + sum(coef_i * feature_i)
      const intercept = predictionInfo.intercept || 0;
      const params = predictionInfo.model_params || [];
      const featureNames: string[] = predictionInfo.poly_feature_names || [];
      const vals: Record<string, number> = {};
      for (const f of factors) vals[f.name] = sliderValues[f.name] ?? (f.min + f.max) / 2;

      // Build polynomial features from raw factor values
      let y = intercept;
      for (let i = 0; i < featureNames.length && i < params.length; i++) {
        const fname = featureNames[i];
        let fval = 1.0;
        // Parse feature name: "Temp", "Time", "Temp^2", "Temp Time"
        const parts = fname.replace(/\^(\d+)/g, (_, exp) => ` ${'@ '.repeat(Number(exp) - 1)}`).split(/\s+/);
        for (const p of parts) {
          if (p === '@') continue; // placeholder for power
          const cleaned = p.trim();
          if (cleaned && vals[cleaned] !== undefined) {
            fval *= vals[cleaned];
          } else if (cleaned) {
            // Try to match partial names
            const match = Object.keys(vals).find(k => cleaned.includes(k) || k.includes(cleaned));
            if (match) fval *= vals[match];
          }
        }
        y += params[i] * fval;
      }
      return y;
    }

    // Factorial: interpolate from level means
    // Simple additive model: Y = grand_mean + sum(effect_i - grand_mean)
    let y = grandMean;
    for (const f of factors) {
      const val = sliderValues[f.name] ?? (f.min + f.max) / 2;
      const effect = f.effect || {};
      const levels = Object.keys(effect).map(Number).sort((a, b) => a - b);
      const means = levels.map(l => effect[String(l)] || grandMean);

      if (levels.length >= 2) {
        // Linear interpolation between nearest levels
        let lowerIdx = 0;
        for (let i = 0; i < levels.length - 1; i++) {
          if (val >= levels[i]) lowerIdx = i;
        }
        const upperIdx = Math.min(lowerIdx + 1, levels.length - 1);
        if (lowerIdx === upperIdx) {
          y += means[lowerIdx] - grandMean;
        } else {
          const t = (val - levels[lowerIdx]) / (levels[upperIdx] - levels[lowerIdx]);
          const interpolated = means[lowerIdx] + t * (means[upperIdx] - means[lowerIdx]);
          y += interpolated - grandMean;
        }
      }
    }
    return y;
  }, [sliderValues, predictionInfo, factors, grandMean]);

  return (
    <div className="card-trendata" style={{ padding: 16 }}>
      <div className="title-bar" style={{ marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>
          因子探索器 — 拖拽因子水平，实时预测响应
        </h4>
      </div>

      {/* Predicted Y display */}
      <div style={{
        textAlign: 'center', padding: '12px 16px', marginBottom: 16, borderRadius: 8,
        background: isDark ? 'rgba(102,126,234,0.1)' : 'rgba(102,126,234,0.05)',
        border: `1px solid ${isDark ? 'rgba(102,126,234,0.25)' : 'rgba(102,126,234,0.15)'}`,
      }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
          预测响应值 ({predictionInfo.equation_type === 'rsm' ? 'RSM 二次模型' : '因子效应模型'})
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--title-color)' }}>
          {isFinite(predictedY) ? predictedY.toFixed(4) : '—'}
        </div>
      </div>

      {/* Factor sliders */}
      <div style={{ display: 'grid', gridTemplateColumns: factors.length <= 3 ? '1fr' : '1fr 1fr', gap: 16 }}>
        {factors.map(f => (
          <div key={f.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{f.name}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--title-color)', fontFamily: 'monospace' }}>
                {(sliderValues[f.name] ?? (f.min + f.max) / 2).toFixed(2)}
              </span>
            </div>
            <Slider
              value={sliderValues[f.name] ?? (f.min + f.max) / 2}
              min={f.min}
              max={f.max}
              step={(f.max - f.min) / 100 || 0.01}
              onChange={(v: number) => setSliderValues(prev => ({ ...prev, [f.name]: v }))}
              tooltip={{ formatter: (v) => v?.toFixed(2) }}
              marks={Object.fromEntries(f.levels.map(l => [l, { style: { fontSize: 9, color: 'var(--text-muted)' }, label: String(l) }]))}
              styles={{ track: { background: '#533afd' }, rail: { background: isDark ? '#333' : '#e8e8e8' } }}
            />
          </div>
        ))}
      </div>

      {/* Reset to center */}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Button size="small" onClick={() => {
          const center: Record<string, number> = {};
          for (const f of factors) center[f.name] = (f.min + f.max) / 2;
          setSliderValues(center);
        }}>重置到中心点</Button>
        {predictionInfo.optimal_settings && (
          <Button size="small" type="primary" style={{ marginLeft: 8 }} onClick={() => {
            const opt: Record<string, number> = {};
            const settings = predictionInfo.optimal_settings;
            for (const f of factors) {
              opt[f.name] = settings[f.name] !== undefined ? settings[f.name] : (f.min + f.max) / 2;
            }
            setSliderValues(opt);
          }}>跳转到最优解</Button>
        )}
      </div>
    </div>
  );
};

// ---- Unified Module Overlay Configs ----
const MODULE_CONFIGS: Record<string, {
  title: string;
  subtitle: string;
  groups: Array<{
    group: string;
    color: string;
    items: Array<{ key: string; name: string; desc: string; icon: string }>;
  }>;
}> = {
  basic_stats: {
    title: '基本统计',
    subtitle: '描述统计、分布检验与数据概览',
    groups: [{ group: '分析内容', color: '#533afd', items: [
      { key: 'descriptive', name: '描述统计', desc: '均值/中位数/标准差/偏度/峰度', icon: 'Σ' },
      { key: 'distribution', name: '分布拟合', desc: '正态/对数正态/Weibull检验', icon: 'Dn' },
      { key: 'visualization', name: '可视化', desc: '直方图+箱线图+QQ图', icon: '📊' },
    ]}],
  },
  process_capability: {
    title: '过程能力分析',
    subtitle: '评估过程满足规格要求的能力',
    groups: [{ group: '分析类型', color: '#6b54fd', items: [
      { key: 'standard', name: '标准过程能力', desc: 'Cp/Cpk/Pp/Ppk + 直方图', icon: 'Cp' },
    ]}],
  },
  spc: {
    title: 'SPC 统计过程控制',
    subtitle: '监控过程稳定性，检测异常模式',
    groups: [
      { group: '计量型控制图', color: '#533afd', items: [
        { key: '', name: '自动推荐', desc: '根据子组大小自动选择', icon: 'AI' },
        { key: 'I-MR', name: 'I-MR 控制图', desc: '个体值+移动极差(n=1)', icon: 'IM' },
        { key: 'Xbar-R', name: 'Xbar-R 控制图', desc: '均值+极差(n=2~8)', icon: 'XR' },
        { key: 'Xbar-S', name: 'Xbar-S 控制图', desc: '均值+标准差(n≥5)', icon: 'XS' },
        { key: 'I-MR-R/S', name: 'I-MR-R/S 三联图', desc: '个值+移动极差+极差/标准差', icon: '3L' },
        { key: 'Z-MR', name: 'Z-MR 标准化', desc: '标准化个值+移动极差', icon: 'ZM' },
      ]},
      { group: '计数型控制图', color: '#f093fb', items: [
        { key: 'p_chart', name: 'P 控制图', desc: '不良率(样本量可变)', icon: 'P' },
        { key: 'np_chart', name: 'NP 控制图', desc: '不良数(样本量固定)', icon: 'NP' },
        { key: 'c_chart', name: 'C 控制图', desc: '缺陷数(固定面积)', icon: 'C' },
        { key: 'u_chart', name: 'U 控制图', desc: '单位缺陷率', icon: 'U' },
      ]},
      { group: '时间加权', color: '#43e97b', items: [
        { key: 'EWMA', name: 'EWMA', desc: '指数加权移动平均，检测小偏移', icon: 'EW' },
        { key: 'CUSUM', name: 'CUSUM', desc: '累积和，检测持续偏移', icon: 'CS' },
      ]},
    ],
  },
  hypothesis: {
    title: '假设检验',
    subtitle: '用统计方法验证总体参数的假设',
    groups: [
      { group: '智能检验', color: '#533afd', items: [
        { key: 'auto', name: '智能决策', desc: 'AI自动选择最佳检验方法', icon: 'AI' },
      ]},
      { group: '均值检验', color: '#4facfe', items: [
        { key: 'one_sample', name: '单样本检验', desc: '样本均值 vs 目标值', icon: '1\u03BC' },
        { key: 'paired', name: '配对检验', desc: '前后/配对样本比较', icon: '\u21D4' },
        { key: 'two_sample', name: '双样本检验', desc: '两组独立样本比较', icon: '2\u03BC' },
        { key: 'multi_sample', name: '多样本检验', desc: '三组及以上比较(ANOVA)', icon: 'N\u03BC' },
        { key: 'two_way_anova', name: '双因素ANOVA', desc: '两个因素+交互效应', icon: '2F' },
      ]},
      { group: '比率与等价', color: '#f093fb', items: [
        { key: 'one_proportion', name: '单比率检验', desc: '比率 vs 目标比率', icon: 'P' },
        { key: 'two_proportion', name: '双比率检验', desc: '两组比率差异比较', icon: '2P' },
        { key: 'variance', name: '方差检验', desc: '方差齐性检验(F/Bartlett)', icon: '\u03C3\u00B2' },
        { key: 'equivalence', name: '等价检验', desc: 'TOST等价性证明', icon: '\u2248' },
      ]},
    ],
  },
  msa: {
    title: '测量系统分析',
    subtitle: '评估测量系统的精确度和准确度',
    groups: [
      { group: '计量型 MSA', color: '#0073e6', items: [
        { key: 'grr', name: 'Gage R&R', desc: '重复性+再现性分析', icon: 'GR' },
        { key: 'bias', name: '偏倚分析', desc: '测量值 vs 基准值偏差', icon: 'Bi' },
        { key: 'linearity', name: '线性分析', desc: '偏倚随量程的变化', icon: 'Li' },
        { key: 'stability', name: '稳定性分析', desc: '测量系统随时间的漂移', icon: 'St' },
      ]},
      { group: '属性型 MSA', color: '#8b5cf6', items: [
        { key: 'attribute', name: '属性一致性', desc: 'Cohen/Fleiss Kappa 评价员一致性', icon: 'κ' },
      ]},
    ],
  },
  regression: {
    title: '回归分析',
    subtitle: '建立变量间的数学关系模型',
    groups: [
      { group: '回归类型', color: '#15be53', items: [
        { key: 'linear', name: '线性回归', desc: '简单线性: 1个X → 1个Y', icon: 'LR' },
        { key: 'polynomial', name: '多项式回归', desc: '1个X，阶数2~5可调', icon: 'Pn' },
        { key: 'multiple', name: '多元回归', desc: 'N个X (可选逐步优化)', icon: 'MR' },
        { key: 'ridge', name: '岭回归', desc: 'L2正则化，处理共线性', icon: 'Rd' },
        { key: 'logistic', name: '逻辑回归', desc: '二分类响应 (0/1)', icon: 'Lo' },
      ]},
      { group: '高级正则化', color: '#f093fb', items: [
        { key: 'lasso', name: 'Lasso回归', desc: 'L1正则化，自动特征筛选', icon: 'La' },
      ]},
      { group: '非线性对比', color: '#8b5cf6', items: [
        { key: 'random_forest', name: '随机森林', desc: '树模型，非线性基线对比', icon: 'RF' },
      ]},
    ],
  },
  time_series: {
    title: '时间序列分析',
    subtitle: '分析数据随时间的变化规律',
    groups: [
      { group: '趋势与分解', color: '#fa709a', items: [
        { key: 'trend', name: '趋势分析', desc: '线性/二次趋势+Mann-Kendall', icon: 'Tr' },
        { key: 'seasonal', name: '季节分解', desc: '加法/乘法自动选择', icon: 'Sn' },
      ]},
      { group: '预测模型', color: '#533afd', items: [
        { key: 'arima', name: 'ARIMA预测', desc: '自回归移动平均模型', icon: 'AR' },
        { key: 'holt_winters', name: 'Holt-Winters', desc: '指数平滑(加法/乘法季节)', icon: 'HW' },
        { key: 'smoothing', name: '平滑法', desc: '移动平均+指数平滑', icon: 'Sm' },
      ]},
    ],
  },
  multivariate: {
    title: '多变量分析',
    subtitle: '探索多个变量间的综合关系',
    groups: [
      { group: '降维与结构', color: '#fee140', items: [
        { key: 'pca', name: 'PCA 主成分', desc: '降维+碎石图+双标图', icon: 'PC' },
        { key: 'factor', name: '因子分析', desc: 'Varimax旋转+载荷矩阵', icon: 'FA' },
        { key: 'correlation', name: '相关性分析', desc: 'Pearson相关矩阵+p值', icon: 'Cr' },
      ]},
      { group: '聚类分析', color: '#84fab0', items: [
        { key: 'cluster', name: 'K-Means聚类', desc: '自动分群+轮廓系数', icon: 'Km' },
        { key: 'hierarchical', name: '层次聚类', desc: 'Ward法+树状图', icon: 'Hc' },
      ]},
      { group: '分类与检验', color: '#f093fb', items: [
        { key: 'discriminant', name: '判别分析', desc: 'LDA线性判别+混淆矩阵', icon: 'DA' },
        { key: 'manova', name: 'MANOVA', desc: '多元方差分析(Wilks\u039B)', icon: 'MA' },
        { key: 'hotelling', name: 'Hotelling T\u00B2', desc: '多元均值检验', icon: 'T\u00B2' },
      ]},
    ],
  },
  reliability: {
    title: '可靠性分析',
    subtitle: '产品寿命与失效模式分析',
    groups: [
      { group: '寿命分析', color: '#a18cd1', items: [
        { key: 'weibull', name: 'Weibull分析', desc: '形状/尺度参数+B10寿命', icon: 'Wb' },
        { key: 'multi_dist', name: '多分布对比', desc: '6种分布AIC/KS比较', icon: 'Md' },
        { key: 'kaplan_meier', name: 'Kaplan-Meier', desc: '生存分析+删失数据', icon: 'KM' },
      ]},
      { group: '工程应用', color: '#43e97b', items: [
        { key: 'demo_test', name: '可靠性验证', desc: '验证是否达标(R\u2265目标)', icon: 'Dm' },
        { key: 'system', name: '系统可靠性', desc: '串联/并联系统MTTF', icon: 'Sy' },
      ]},
    ],
  },
  nonparametric: {
    title: '非参数分析',
    subtitle: '不依赖分布假设的统计检验',
    groups: [
      { group: '离群值检测', color: '#fbc2eb', items: [
        { key: 'grubbs', name: 'Grubbs检验', desc: '单个极端值检测', icon: 'Gb' },
        { key: 'dixon', name: 'Dixon检验', desc: '小样本离群值(n\u226425)', icon: 'Dx' },
        { key: 'iqr', name: 'IQR检测', desc: 'Tukey箱线图法', icon: 'IQ' },
      ]},
      { group: '位置检验', color: '#84fab0', items: [
        { key: 'sign_test', name: '符号检验', desc: '单样本中位数检验+CI', icon: 'Sg' },
        { key: 'mann_whitney', name: 'Mann-Whitney U', desc: '两组非参数比较', icon: 'MW' },
        { key: 'kruskal', name: 'Kruskal-Wallis', desc: '多组非参数比较', icon: 'KW' },
        { key: 'moods_median', name: 'Mood中位数', desc: '多组中位数比较', icon: 'Md' },
        { key: 'friedman', name: 'Friedman检验', desc: '重复测量非参数ANOVA', icon: 'Fr' },
      ]},
      { group: '方差与分布', color: '#4facfe', items: [
        { key: 'levene', name: 'Levene检验', desc: 'Brown-Forsythe方差齐性', icon: 'Lv' },
        { key: 'ks_2sample', name: 'KS双样本', desc: '两组分布形态比较', icon: 'KS' },
        { key: 'runs_test', name: '游程检验', desc: '序列随机性检验', icon: 'Rn' },
      ]},
    ],
  },
  chi_square: {
    title: '卡方分析',
    subtitle: '分类变量的关联性检验',
    groups: [
      { group: '关联检验', color: '#84fab0', items: [
        { key: 'independence', name: '独立性检验', desc: '两个分类变量的关联', icon: '\u03C7\u00B2' },
        { key: 'fisher', name: 'Fisher精确检验', desc: '2×2表精确检验(小样本)', icon: 'Fe' },
        { key: 'cross_tab', name: '交叉表分析', desc: '行/列百分比+标准化残差', icon: 'Ct' },
      ]},
      { group: '配对与拟合', color: '#f093fb', items: [
        { key: 'mcnemar', name: 'McNemar检验', desc: '配对分类数据前后比较', icon: 'Mc' },
        { key: 'goodness', name: '拟合优度', desc: '观测vs期望分布', icon: 'Gf' },
      ]},
    ],
  },
};

// ---- Unified Module Overlay Component ----
const ModuleOverlay: React.FC<{
  moduleName: string;
  isDark: boolean;
  onClose: () => void;
  onExecute: (subType: string, extraParams: Record<string, any>) => void;
  columns: ColumnInfo[];
  selectedCols: string[];
  onSelectedColsChange: (cols: string[]) => void;
  usl: number | null;
  lsl: number | null;
  onUslChange: (v: number | null) => void;
  onLslChange: (v: number | null) => void;
  spcChartType: string;
  onSpcChartTypeChange: (v: string) => void;
}> = ({ moduleName, isDark, onClose, onExecute, columns, selectedCols, onSelectedColsChange, usl, lsl, onUslChange, onLslChange, spcChartType, onSpcChartTypeChange }) => {
  const config = MODULE_CONFIGS[moduleName];
  const [localSubType, setLocalSubType] = useState(() => {
    if (moduleName === 'spc') return spcChartType;
    if (moduleName === 'hypothesis') return 'auto';
    // Default to first item of first group
    const firstItem = config?.groups?.[0]?.items?.[0];
    return firstItem?.key || '';
  });
  const [alpha, setAlpha] = useState(0.05);
  const [testValue, setTestValue] = useState<number | null>(null);
  const [targetCol, setTargetCol] = useState('');
  // Hypothesis: explicit factor/measurement role selectors
  const [hypoGroupCol, setHypoGroupCol] = useState('');
  const [hypoValueCol, setHypoValueCol] = useState('');
  // Regression: explicit X/Y selectors + sub-params
  const [regYCol, setRegYCol] = useState('');
  const [regXCols, setRegXCols] = useState<string[]>([]);
  const [polyDegree, setPolyDegree] = useState(2);
  const [stepwiseEnabled, setStepwiseEnabled] = useState(false);
  const [stepwiseDirection, setStepwiseDirection] = useState<'forward' | 'backward' | 'both'>('forward');
  const [ridgeAlpha, setRidgeAlpha] = useState(1.0);
  const [lassoAlpha, setLassoAlpha] = useState(0.1);
  // Process capability: target + boundary + subgroup
  const [localTarget, setLocalTarget] = useState<number | null>(null);
  const [lslBoundary, setLslBoundary] = useState(false);
  const [uslBoundary, setUslBoundary] = useState(false);
  const [localSubgroupMode, setLocalSubgroupMode] = useState<'none' | 'size' | 'column'>('none');
  const [localSubgroupSize, setLocalSubgroupSize] = useState(5);
  const [localSubgroupCol, setLocalSubgroupCol] = useState('');
  const [dataNatureOverride, setDataNatureOverride] = useState<'auto' | 'natural_nonnormal' | 'should_be_normal'>('auto');
  const [transformMethod, setTransformMethod] = useState('auto');
  // SPC: Nelson rules, X column, subgroup, phase
  const [nelsonRules, setNelsonRules] = useState<number[]>([1]);
  const [xColumn, setXColumn] = useState('');
  const [spcSubgroupSize, setSpcSubgroupSize] = useState(() => {
    if (moduleName !== 'spc') return 1;
    if (spcChartType === 'Xbar-R') return 2;
    if (spcChartType === 'Xbar-S') return 5;
    return 1;
  });
  // Attribute chart column overrides
  const [attrDefectCol, setAttrDefectCol] = useState('');
  const [attrSampleCol, setAttrSampleCol] = useState('');
  const [attrUnitCol, setAttrUnitCol] = useState('');
  const [phaseMode, setPhaseMode] = useState<'none' | 'column' | 'manual'>('none');
  const [phaseColumn, setPhaseColumn] = useState('');
  const [phaseSplit, setPhaseSplit] = useState('');
  const [phaseBaseline, setPhaseBaseline] = useState(false);
  const [ewmaLambda, setEwmaLambda] = useState(0.2);
  const [ewmaL, setEwmaL] = useState(3.0);
  const [cusumK, setCusumK] = useState(0.5);
  const [cusumH, setCusumH] = useState(5.0);

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!config) return null;

  const hasMultipleTypes = config.groups.reduce((sum, g) => sum + g.items.length, 0) > 1;

  const handleExecute = () => {
    const extra: Record<string, any> = {};

    if (moduleName === 'process_capability') {
      if (usl === null || lsl === null) return;
      if (usl <= lsl) return;
      if (localTarget !== null) extra.target = localTarget;
      if (lslBoundary) extra.lsl_boundary = true;
      if (uslBoundary) extra.usl_boundary = true;
      if (localSubgroupMode === 'size' && localSubgroupSize > 1) extra.subgroup_size = localSubgroupSize;
      if (localSubgroupMode === 'column' && localSubgroupCol) extra.subgroup_col = localSubgroupCol;
      if (dataNatureOverride !== 'auto') extra.data_nature_override = dataNatureOverride;
    }
    if (moduleName === 'spc') {
      onSpcChartTypeChange(localSubType);
      extra.spc_chart_type = localSubType;
      extra.nelson_rules = nelsonRules;
      if (xColumn) extra.x_column = xColumn;
      if (spcSubgroupSize > 1) extra.subgroup_size = spcSubgroupSize;
      if (['p_chart','np_chart','c_chart','u_chart'].includes(localSubType)) {
        if (attrDefectCol) extra.defect_col = attrDefectCol;
        if (attrSampleCol) extra.sample_col = attrSampleCol;
        if (attrUnitCol) extra.unit_col = attrUnitCol;
      }
      if (phaseMode === 'manual' && phaseSplit.trim()) {
        extra.phase_split = phaseSplit.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
      }
      if (phaseMode === 'column' && phaseColumn) extra.phase_column = phaseColumn;
      if (phaseMode !== 'none' && phaseBaseline) extra.phase_baseline = true;
      if (localSubType === 'EWMA') {
        extra.ewma_lambda = ewmaLambda;
        extra.ewma_L = ewmaL;
      }
      if (localSubType === 'CUSUM') {
        extra.cusum_k = cusumK;
        extra.cusum_h = cusumH;
      }
    }
    if (moduleName === 'hypothesis') {
      extra.test_type = localSubType || 'auto';
      extra.alpha = alpha;
      if (localSubType === 'one_sample' && testValue !== null) extra.test_value = testValue;
      if (hypoGroupCol) extra.group_col = hypoGroupCol;
      if (hypoValueCol) extra.value_col = hypoValueCol;
    }
    if (moduleName === 'msa') extra.msa_type = localSubType;
    if (moduleName === 'regression') {
      extra.regression_type = localSubType;
      if (regYCol) extra.y_col = regYCol;
      if (regXCols.length > 0) extra.x_cols = regXCols;
      if (localSubType === 'polynomial') extra.poly_degree = polyDegree;
      if (localSubType === 'multiple') {
        extra.stepwise_enabled = stepwiseEnabled;
        extra.stepwise_direction = stepwiseDirection;
      }
      if (localSubType === 'ridge') extra.ridge_alpha = ridgeAlpha;
      if (localSubType === 'lasso') extra.lasso_alpha = lassoAlpha;
      // Sync analysis columns set so backend topology + cache key reflect Y∪X
      const regressionCols = regYCol ? [regYCol, ...regXCols.filter(c => c !== regYCol)] : regXCols;
      if (regressionCols.length > 0) {
        extra.columns = regressionCols;
        onSelectedColsChange(regressionCols);
      }
    }
    if (moduleName === 'time_series') extra.ts_method = localSubType;
    if (moduleName === 'multivariate') extra.mv_method = localSubType;
    if (moduleName === 'reliability') extra.reliability_method = localSubType;
    if (moduleName === 'nonparametric') {
      extra.np_method = localSubType;
      extra.alpha = alpha;
    }
    if (moduleName === 'chi_square') extra.chi_method = localSubType;

    onExecute(localSubType, extra);
  };

  const numericCols = columns.filter(c => ['float64', 'int64', 'float', 'integer', 'number'].includes(c.dtype));

  return (<>
    {/* Dim backdrop */}
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 999,
      background: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.12)',
      animation: 'fadeIn 0.15s ease',
    }} />
    {/* Floating panel */}
    <div style={{
      position: 'fixed', zIndex: 1000,
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: 720, maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 60px)',
      overflowY: 'auto',
      background: isDark ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.88)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 18, padding: '24px 28px 20px',
      boxShadow: isDark
        ? '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)'
        : '0 24px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
      animation: 'modalIn 0.2s ease',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--title-color)', margin: 0 }}>{config.title}</h2>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>{config.subtitle}</p>
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={onClose}
          style={{ width: 32, height: 32, minWidth: 32, borderRadius: 8, color: 'var(--text-muted)' }} />
      </div>

      {/* Type cards */}
      {hasMultipleTypes && config.groups.map(group => (
        <div key={group.group} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: group.color, letterSpacing: 0.8,
            textTransform: 'uppercase', marginBottom: 6 }}>{group.group}</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(group.items.length, 3)}, 1fr)`, gap: 8 }}>
            {group.items.map(item => {
              const sel = localSubType === item.key;
              return (
                <div key={item.key} onClick={() => {
                  setLocalSubType(item.key);
                  // Auto-set subgroup size for SPC chart types
                  if (moduleName === 'spc') {
                    if (item.key === 'Xbar-R') setSpcSubgroupSize(2);
                    else if (item.key === 'Xbar-S') setSpcSubgroupSize(5);
                    else if (item.key === 'I-MR-R/S') { if (spcSubgroupSize < 2) setSpcSubgroupSize(2); }
                    else setSpcSubgroupSize(1);
                  }
                }}
                  style={{
                    padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: sel
                      ? (isDark ? `${group.color}20` : `${group.color}0c`)
                      : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    border: `1.5px solid ${sel ? group.color : 'transparent'}`,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'; }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: sel ? group.color : (isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'),
                    color: sel ? '#fff' : group.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 800, fontFamily: 'monospace',
                    transition: 'all 0.15s',
                  }}>{item.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: sel ? group.color : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Divider */}
      <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', margin: '14px 0' }} />

      {/* Module-specific config */}
      {moduleName === 'process_capability' && (<>
        {/* Row 1: LSL → USL → Target */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
              规格下限 (LSL) <span style={{ color: '#f5222d' }}>*</span>
            </div>
            <InputNumber value={lsl} onChange={v => onLslChange(v)} style={{ width: '100%' }} placeholder="下限" size="small" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, cursor: 'pointer', fontSize: 10, color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={lslBoundary} onChange={e => setLslBoundary(e.target.checked)}
                style={{ width: 12, height: 12, accentColor: '#533afd' }} />
              自然边界 (如 0)
            </label>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
              规格上限 (USL) <span style={{ color: '#f5222d' }}>*</span>
            </div>
            <InputNumber value={usl} onChange={v => onUslChange(v)} style={{ width: '100%' }} placeholder="上限" size="small" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, cursor: 'pointer', fontSize: 10, color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={uslBoundary} onChange={e => setUslBoundary(e.target.checked)}
                style={{ width: 12, height: 12, accentColor: '#533afd' }} />
              自然边界
            </label>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
              目标值 (Target)
            </div>
            <InputNumber value={localTarget} onChange={v => setLocalTarget(v)} style={{ width: '100%' }} placeholder="选填" size="small" />
            <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>留空则不计算 Cpm</div>
          </div>
        </div>
        {/* Row 2: Subgroup */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>子组设置</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {([['none', '无子组'], ['size', '固定大小'], ['column', '子组列']] as const).map(([k, label]) => (
                <div key={k} onClick={() => { setLocalSubgroupMode(k); if (k === 'none') { setLocalSubgroupSize(1); setLocalSubgroupCol(''); }}}
                  style={{
                    flex: 1, padding: '5px 4px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                    background: localSubgroupMode === k ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                    color: localSubgroupMode === k ? '#fff' : 'var(--text-muted)',
                    fontSize: 10, fontWeight: 600, transition: 'all 0.12s',
                  }}>{label}</div>
              ))}
            </div>
          </div>
          <div>
            {localSubgroupMode === 'size' && (<>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>子组大小 (n)</div>
              <InputNumber value={localSubgroupSize} onChange={v => setLocalSubgroupSize(v || 1)} min={2} max={25} size="small" style={{ width: '100%' }} />
            </>)}
            {localSubgroupMode === 'column' && (<>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>子组列</div>
              <Select value={localSubgroupCol || undefined} onChange={setLocalSubgroupCol} size="small" style={{ width: '100%' }}
                placeholder="选择" options={columns.filter(c => c.unique !== undefined && c.unique <= 200).map(c => ({ value: c.name, label: `${c.name} (${c.unique}组)` }))} />
            </>)}
            {localSubgroupMode === 'none' && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', paddingTop: 18 }}>个体值分析 (n=1)，使用移动极差估计 σ</div>
            )}
          </div>
        </div>
        {/* Boundary tip */}
        {(lslBoundary || uslBoundary) && (
          <div style={{ fontSize: 10, color: isDark ? '#f5a623' : '#d48806', marginBottom: 10, padding: '6px 10px', borderRadius: 6,
            background: isDark ? 'rgba(245,166,35,0.08)' : 'rgba(245,166,35,0.06)',
            border: `1px solid ${isDark ? 'rgba(245,166,35,0.2)' : 'rgba(245,166,35,0.12)'}`,
          }}>
            已标记自然边界：数据在该边界附近可能不服从正态分布，系统将自动启用分布识别引擎，使用实际分布计算 Cnpk。
          </div>
        )}
        {/* Data nature override */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.5 }}>数据性质</span>
            <Tooltip title="如果数据检测为非正态，选择数据的本质特性决定分析方法"><span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span></Tooltip>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {([
              ['auto', '自动判断', '系统自动检测正态性和数据本质'],
              ['natural_nonnormal', '天然非正态', '寿命/缺陷率等本质非正态数据 → 用最佳拟合分布计算 Cnpk'],
              ['should_be_normal', '理论正态', '加工尺寸等理论正态数据 → Box-Cox转换后计算 Cpk'],
            ] as const).map(([key, label, tip]) => (
              <Tooltip key={key} title={tip}>
                <div onClick={() => setDataNatureOverride(key)}
                  style={{
                    flex: 1, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                    fontSize: 10, fontWeight: 600,
                    background: dataNatureOverride === key ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                    color: dataNatureOverride === key ? '#fff' : 'var(--text-muted)',
                    transition: 'all 0.12s',
                  }}>{label}</div>
              </Tooltip>
            ))}
          </div>
          {dataNatureOverride === 'natural_nonnormal' && (
            <div style={{ fontSize: 9, color: isDark ? '#43e97b' : '#389e0d', marginTop: 4, padding: '4px 8px', borderRadius: 4,
              background: isDark ? 'rgba(67,233,123,0.06)' : 'rgba(67,233,123,0.04)' }}>
              将使用个体分布识别结果（Weibull/对数正态等）直接计算非正态能力指数 Cnpk，不做数据转换。
            </div>
          )}
          {dataNatureOverride === 'should_be_normal' && (
            <div style={{ fontSize: 9, color: isDark ? '#4facfe' : '#1890ff', marginTop: 4, padding: '6px 10px', borderRadius: 6,
              background: isDark ? 'rgba(79,172,254,0.06)' : 'rgba(79,172,254,0.04)',
              border: `1px solid ${isDark ? 'rgba(79,172,254,0.15)' : 'rgba(79,172,254,0.1)'}`,
            }}>
              分析流程：先用原始数据计算 Cpk 并做个体分布识别 → 查看分布拟合对比图 → 根据最佳拟合分布决定转换方式后重新分析。
              报告中会标注数据非正态，给出转换建议。
            </div>
          )}
        </div>
      </>)}

      {moduleName === 'spc' && (<>
        {/* Row 1: Subgroup + X column (hidden for attribute charts) */}
        {!['p_chart', 'np_chart', 'c_chart', 'u_chart', 'EWMA', 'CUSUM'].includes(localSubType) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.5 }}>子组大小</span>
              {(localSubType === 'Xbar-R' || localSubType === 'Xbar-S') && (
                <Tooltip title={localSubType === 'Xbar-R'
                  ? 'Xbar-R: 子组2~8个，用极差(R)估计σ。推荐n=4~5。子组=1请用I-MR图。'
                  : 'Xbar-S: 子组≥5个，用标准差(S)估计σ。推荐n=5~10。子组<5请用Xbar-R图。'
                }>
                  <span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span>
                </Tooltip>
              )}
            </div>
            <InputNumber value={spcSubgroupSize} onChange={v => setSpcSubgroupSize(v || 1)}
              min={localSubType === 'Xbar-R' ? 2 : localSubType === 'Xbar-S' ? 5 : 1} max={50}
              size="small" style={{ width: '100%' }}
              status={(localSubType === 'Xbar-R' && spcSubgroupSize < 2) || (localSubType === 'Xbar-S' && spcSubgroupSize < 5) ? 'error' : undefined}
              placeholder={localSubType === 'I-MR' ? '1 (个体值)' : localSubType === 'Xbar-R' ? '2~8 (必填)' : localSubType === 'Xbar-S' ? '≥5 (必填)' : '自动'} />
            {localSubType === 'Xbar-R' && spcSubgroupSize < 2 && (
              <div style={{ fontSize: 9, color: '#f5222d', marginTop: 2 }}>Xbar-R 图要求子组 ≥ 2</div>
            )}
            {localSubType === 'Xbar-S' && spcSubgroupSize < 5 && (
              <div style={{ fontSize: 9, color: '#f5222d', marginTop: 2 }}>Xbar-S 图要求子组 ≥ 5</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>X轴 (时间/序号列)</div>
            <Select value={xColumn || undefined} onChange={setXColumn} allowClear placeholder="默认: 1,2,3..."
              size="small" style={{ width: '100%' }}
              options={columns.map(c => ({ value: c.name, label: c.name }))} />
          </div>
        </div>
        )}
        {localSubType === 'EWMA' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>权重 λ (Lambda)</span>
                <Tooltip title="越小越平滑，检测慢偏移。推荐0.05~0.25，默认0.2"><span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span></Tooltip>
              </div>
              <InputNumber value={ewmaLambda} onChange={v => setEwmaLambda(v || 0.2)} min={0.01} max={1} step={0.05} size="small" style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>控制限宽度 L</span>
                <Tooltip title="控制限的σ倍数。推荐2.7~3.0，默认3.0"><span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span></Tooltip>
              </div>
              <InputNumber value={ewmaL} onChange={v => setEwmaL(v || 3.0)} min={1} max={5} step={0.1} size="small" style={{ width: '100%' }} />
            </div>
          </div>
        )}

        {localSubType === 'CUSUM' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>容许值 k (σ倍数)</span>
                <Tooltip title="允许的偏移量，以σ为单位。推荐0.5(检测1σ偏移)，默认0.5"><span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span></Tooltip>
              </div>
              <InputNumber value={cusumK} onChange={v => setCusumK(v || 0.5)} min={0.1} max={2} step={0.1} size="small" style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>决策区间 h (σ倍数)</span>
                <Tooltip title="累积和超过h则报警。推荐4~5，默认5"><span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span></Tooltip>
              </div>
              <InputNumber value={cusumH} onChange={v => setCusumH(v || 5)} min={1} max={10} step={0.5} size="small" style={{ width: '100%' }} />
            </div>
          </div>
        )}

        {/* Attribute chart column pickers */}
        {['p_chart', 'np_chart', 'c_chart', 'u_chart'].includes(localSubType) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
            {/* Defect/defective column - all attribute charts need this */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                {localSubType === 'c_chart' || localSubType === 'u_chart' ? '缺陷数列' : '不良数列'}
              </div>
              <Select value={attrDefectCol || undefined} onChange={setAttrDefectCol} allowClear
                placeholder="自动检测 (defects/不良数/缺陷数)"
                size="small" style={{ width: '100%' }}
                options={numericCols.map(c => ({ value: c.name, label: c.name }))} />
            </div>
            {/* Sample size column - P and NP charts */}
            {(localSubType === 'p_chart' || localSubType === 'np_chart') && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  样本量列
                </div>
                <Select value={attrSampleCol || undefined} onChange={setAttrSampleCol} allowClear
                  placeholder="自动检测 (sample_size/样本量/检验数)"
                  size="small" style={{ width: '100%' }}
                  options={numericCols.map(c => ({ value: c.name, label: c.name }))} />
              </div>
            )}
            {/* Units column - U chart */}
            {localSubType === 'u_chart' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  检验单位列
                </div>
                <Select value={attrUnitCol || undefined} onChange={setAttrUnitCol} allowClear
                  placeholder="自动检测 (units/检验单位/检验面积)"
                  size="small" style={{ width: '100%' }}
                  options={numericCols.map(c => ({ value: c.name, label: c.name }))} />
              </div>
            )}
            {/* X column for attribute charts too */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>X轴 (时间/序号列)</div>
              <Select value={xColumn || undefined} onChange={setXColumn} allowClear placeholder="默认: 1,2,3..."
                size="small" style={{ width: '100%' }}
                options={columns.map(c => ({ value: c.name, label: c.name }))} />
            </div>
          </div>
        )}
        {/* Row 2: Nelson rules */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5, letterSpacing: 0.5 }}>
            判异准则 (Nelson Rules)
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {[
              { n: 1, label: '超限', tip: '点超出3σ控制限' },
              { n: 2, label: '连串', tip: '连续9点在中心线同侧' },
              { n: 3, label: '趋势', tip: '连续6点递增或递减' },
              { n: 4, label: '振荡', tip: '连续14点上下交替' },
              { n: 5, label: '2/3超2σ', tip: '3点中2点超出2σ(同侧)' },
              { n: 6, label: '4/5超1σ', tip: '5点中4点超出1σ(同侧)' },
              { n: 7, label: '分层', tip: '连续15点在±1σ内' },
              { n: 8, label: '混合', tip: '连续8点在±1σ外' },
            ].map(r => {
              const sel = nelsonRules.includes(r.n);
              return (
                <Tooltip key={r.n} title={`规则${r.n}: ${r.tip}`}>
                  <div onClick={() => setNelsonRules(prev => sel ? prev.filter(x => x !== r.n) : [...prev, r.n])}
                    style={{
                      padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                      background: sel ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                      color: sel ? '#fff' : 'var(--text-muted)',
                      transition: 'all 0.12s',
                      border: `1px solid ${sel ? '#533afd' : 'transparent'}`,
                    }}>
                    {r.n}. {r.label}
                  </div>
                </Tooltip>
              );
            })}
            <span onClick={() => setNelsonRules(nelsonRules.length === 8 ? [1] : [1,2,3,4,5,6,7,8])}
              style={{ fontSize: 10, color: '#533afd', cursor: 'pointer', padding: '3px 6px' }}>
              {nelsonRules.length === 8 ? '仅规则1' : '全选'}
            </span>
          </div>
        </div>
        {/* Row 3: Phase control */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.5 }}>阶段控制图</span>
            <Tooltip title="将数据分成多个阶段（如改善前/后），每段独立计算控制限，直观对比过程改进效果">
              <span style={{ fontSize: 11, cursor: 'help', color: '#533afd' }}>?</span>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            {([['none', '不分阶段'], ['column', '按列分阶段'], ['manual', '手动分界']] as const).map(([k, label]) => (
              <div key={k} onClick={() => setPhaseMode(k)}
                style={{
                  padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                  background: phaseMode === k ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                  color: phaseMode === k ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.12s',
                }}>{label}</div>
            ))}
          </div>
          {phaseMode === 'column' && (
            <div>
              <Select value={phaseColumn || undefined} onChange={setPhaseColumn} size="small" style={{ width: '100%' }}
                placeholder="选择阶段列（如: Phase、改善前/后）"
                options={columns.filter(c => c.unique !== undefined && c.unique >= 2 && c.unique <= 10)
                  .map(c => ({ value: c.name, label: `${c.name} (${c.unique}个阶段)` }))} />
              <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                列值变化处自动切分阶段，每段独立计算控制限
              </div>
            </div>
          )}
          {phaseMode === 'manual' && (
            <div>
              <input value={phaseSplit} onChange={e => setPhaseSplit(e.target.value)}
                placeholder="分界行号，如: 25 表示第25行后切分为两阶段"
                style={{
                  width: '100%', padding: '4px 8px', borderRadius: 6, fontSize: 11,
                  border: `1px solid ${isDark ? '#444' : '#d9d9d9'}`, outline: 'none',
                  background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                  color: 'var(--text-primary)',
                }} />
              <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                多个分界点用逗号分隔。如 "25" = 改善前(1-25) + 改善后(26+)
              </div>
            </div>
          )}
          {phaseMode !== 'none' && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {[
                { key: false, label: '独立控制限', tip: '每段独立计算CL/UCL/LCL' },
                { key: true, label: '基线控制限', tip: '用第一阶段的限作为标准评判后续阶段' },
              ].map(opt => (
                <Tooltip key={String(opt.key)} title={opt.tip}>
                  <div onClick={() => setPhaseBaseline(opt.key)}
                    style={{
                      flex: 1, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                      fontSize: 10, fontWeight: 600,
                      background: phaseBaseline === opt.key ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                      color: phaseBaseline === opt.key ? '#fff' : 'var(--text-muted)',
                      transition: 'all 0.12s',
                    }}>{opt.label}</div>
                </Tooltip>
              ))}
            </div>
          )}
          {phaseMode !== 'none' && phaseBaseline && (
            <div style={{ fontSize: 9, color: isDark ? '#f5a623' : '#d48806', marginTop: 4, padding: '4px 8px',
              borderRadius: 4, background: isDark ? 'rgba(245,166,35,0.06)' : 'rgba(245,166,35,0.04)' }}>
              基线模式：后续阶段的数据点如果超出第一阶段的控制限，将被标记为异常——即使该点在自身阶段的±3σ内。
            </div>
          )}
        </div>
      </>)}

      {moduleName === 'hypothesis' && (
        <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: localSubType === 'one_sample' ? '1fr 1fr' : '1fr', gap: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                显著性水平 (\u03B1)
              </div>
              <Select value={alpha} onChange={setAlpha} size="small" style={{ width: '100%' }}
                options={[
                  { value: 0.01, label: '\u03B1 = 0.01 (99%)' },
                  { value: 0.05, label: '\u03B1 = 0.05 (95%)' },
                  { value: 0.10, label: '\u03B1 = 0.10 (90%)' },
                ]}
              />
            </div>
            {localSubType === 'one_sample' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  检验值 (\u03BC\u2080)
                </div>
                <InputNumber value={testValue} onChange={v => setTestValue(v)} style={{ width: '100%' }} placeholder="H\u2080: \u03BC = ?" size="small" />
              </div>
            )}
          </div>
          {/* Role picker: useful when numeric columns are actually factors (e.g. Catalyst=1,2,3) */}
          {['auto', 'two_way_anova'].includes(localSubType) && (
            <div style={{ padding: '10px 12px', borderRadius: 8,
              background: isDark ? 'rgba(223,27,65,0.06)' : 'rgba(223,27,65,0.03)',
              border: `1px solid ${isDark ? 'rgba(223,27,65,0.2)' : 'rgba(223,27,65,0.12)'}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 0.5 }}>
                列角色（可选）— 当数值列实为分组因子时指定
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>分组列（因子）</div>
                  <Select value={hypoGroupCol || undefined}
                    onChange={v => { setHypoGroupCol(v); if (v === hypoValueCol) setHypoValueCol(''); }}
                    allowClear placeholder="自动检测" style={{ width: '100%' }} size="small"
                    options={columns.map(c => ({ value: c.name, label: `${c.name} (${c.dtype === 'object' ? 'cat' : c.dtype})` }))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>测量列（数值）</div>
                  <Select value={hypoValueCol || undefined}
                    onChange={v => { setHypoValueCol(v); if (v === hypoGroupCol) setHypoGroupCol(''); }}
                    allowClear placeholder="自动检测" style={{ width: '100%' }} size="small"
                    options={numericCols.filter(c => c.name !== hypoGroupCol).map(c => ({ value: c.name, label: c.name }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {moduleName === 'nonparametric' && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
            显著性水平 (\u03B1)
          </div>
          <Select value={alpha} onChange={setAlpha} size="small" style={{ width: 200 }}
            options={[
              { value: 0.01, label: '\u03B1 = 0.01' },
              { value: 0.05, label: '\u03B1 = 0.05' },
              { value: 0.10, label: '\u03B1 = 0.10' },
            ]}
          />
        </div>
      )}

      {moduleName === 'regression' && (() => {
        const isSingleX = localSubType === 'linear' || localSubType === 'polynomial';
        const xOptions = numericCols.filter(c => c.name !== regYCol).map(c => ({ value: c.name, label: c.name }));
        return (
          <div style={{ marginBottom: 14, display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                响应变量 Y {localSubType === 'logistic' ? '(二分类: 0/1 或两类)' : '(数值列)'}
              </div>
              <Select value={regYCol || undefined} onChange={v => { setRegYCol(v); setRegXCols(prev => prev.filter(x => x !== v)); }}
                allowClear placeholder="选择响应变量" style={{ width: '100%' }} size="small"
                options={numericCols.map(c => ({ value: c.name, label: c.name }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                自变量 X {isSingleX ? '(只能选 1 个)' : '(可多选)'}
              </div>
              <Select mode={isSingleX ? undefined : 'multiple'}
                value={isSingleX ? (regXCols[0] || undefined) : regXCols}
                onChange={(v: any) => setRegXCols(isSingleX ? (v ? [v] : []) : (v as string[]))}
                allowClear placeholder={isSingleX ? '选择 1 个自变量' : '选择 1 或多个自变量'}
                style={{ width: '100%' }} size="small" options={xOptions}
              />
            </div>
            {localSubType === 'polynomial' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  多项式阶数: {polyDegree}
                </div>
                <Slider min={1} max={5} step={1} value={polyDegree} onChange={setPolyDegree}
                  marks={{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5' }} />
              </div>
            )}
            {localSubType === 'multiple' && (
              <div style={{ display: 'grid', gap: 6, padding: '10px 12px', borderRadius: 8,
                background: isDark ? 'rgba(21,190,83,0.08)' : 'rgba(21,190,83,0.05)',
                border: `1px solid ${isDark ? 'rgba(21,190,83,0.2)' : 'rgba(21,190,83,0.15)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-primary)', fontWeight: 600 }}>启用逐步回归 (变量选择优化)</span>
                  <Switch size="small" checked={stepwiseEnabled} onChange={setStepwiseEnabled} />
                </div>
                {stepwiseEnabled && (
                  <Segmented size="small" value={stepwiseDirection}
                    onChange={(v) => setStepwiseDirection(v as any)}
                    options={[
                      { label: '前向', value: 'forward' },
                      { label: '后向', value: 'backward' },
                      { label: '双向', value: 'both' },
                    ]}
                  />
                )}
              </div>
            )}
            {localSubType === 'ridge' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  L2 正则化强度 α: {ridgeAlpha}
                </div>
                <Slider min={0.01} max={10} step={0.01} value={ridgeAlpha} onChange={setRidgeAlpha}
                  marks={{ 0.01: '0.01', 1: '1', 5: '5', 10: '10' }} />
              </div>
            )}
            {localSubType === 'lasso' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>
                  L1 正则化强度 α: {lassoAlpha}
                </div>
                <Slider min={0.001} max={1} step={0.001} value={lassoAlpha} onChange={setLassoAlpha}
                  marks={{ 0.001: '0.001', 0.1: '0.1', 0.5: '0.5', 1: '1' }} />
              </div>
            )}
          </div>
        );
      })()}

      {moduleName === 'msa' && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, padding: '8px 12px', borderRadius: 8,
          background: isDark ? 'rgba(79,172,254,0.08)' : 'rgba(79,172,254,0.05)',
          border: `1px solid ${isDark ? 'rgba(79,172,254,0.2)' : 'rgba(79,172,254,0.1)'}`,
        }}>
          请确保选择的数据列包含零件/操作员/测量值信息，系统将自动识别列角色。
        </div>
      )}

      {/* Column selection */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.5 }}>
            分析列 — 点击选择要分析的数据列 ({selectedCols.length}/{columns.length})
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span onClick={() => onSelectedColsChange(numericCols.map(c => c.name))}
              style={{ fontSize: 10, color: '#533afd', cursor: 'pointer' }}>全选数值</span>
            <span onClick={() => onSelectedColsChange(columns.map(c => c.name))}
              style={{ fontSize: 10, color: '#533afd', cursor: 'pointer' }}>全选</span>
            <span onClick={() => onSelectedColsChange([])}
              style={{ fontSize: 10, color: '#999', cursor: 'pointer' }}>清空</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {columns.map(c => {
            const sel = selectedCols.includes(c.name);
            const isNum = ['float64', 'int64', 'float', 'integer', 'number'].includes(c.dtype);
            return (
              <div key={c.name} onClick={() => {
                onSelectedColsChange(sel ? selectedCols.filter(x => x !== c.name) : [...selectedCols, c.name]);
              }}
                style={{
                  padding: '3px 9px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 500,
                  background: sel ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                  color: sel ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.12s',
                }}>
                {c.name} <span style={{ opacity: 0.6, fontSize: 9 }}>{isNum ? c.dtype.slice(0, 3) : 'cat'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose} style={{ borderRadius: 8 }}>取消</Button>
        <Button type="primary" style={{ borderRadius: 8, minWidth: 120 }}
          disabled={
            (moduleName === 'regression'
              ? (!regYCol || regXCols.length === 0)
              : selectedCols.length === 0)
            || (moduleName === 'process_capability' && (usl === null || lsl === null || (usl !== null && lsl !== null && Number(usl) <= Number(lsl))))
            || (moduleName === 'spc' && localSubType === 'Xbar-R' && spcSubgroupSize < 2)
            || (moduleName === 'spc' && localSubType === 'Xbar-S' && spcSubgroupSize < 5)
            || (moduleName === 'spc' && localSubType === 'I-MR-R/S' && spcSubgroupSize < 2)
          }
          onClick={handleExecute}>
          开始分析 {moduleName === 'regression'
            ? (regYCol && regXCols.length > 0 ? `(Y=${regYCol}, ${regXCols.length}个X)` : '')
            : (selectedCols.length > 0 && `(${selectedCols.length}列)`)}
        </Button>
      </div>
    </div>
  </>);
};

const AnalysisWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // File
  const [fileId, setFileId] = useState(searchParams.get('fileId') || '');
  const [fileName, setFileName] = useState(searchParams.get('fileName') || '');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');

  // Data
  const [dataLoading, setDataLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [missingStrategy, setMissingStrategy] = useState('none');

  // Subgroup config
  const [subgroupMode, setSubgroupMode] = useState<'none' | 'column' | 'size'>('none');
  const [subgroupCol, setSubgroupCol] = useState('');
  const [subgroupSize, setSubgroupSize] = useState(1);

  // Unified overlay for all modules (except DOE which has its own)
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);
  const [usl, setUsl] = useState<number | null>(null);
  const [lsl, setLsl] = useState<number | null>(null);
  const [pendingModule, setPendingModule] = useState('');
  const [spcChartType, setSpcChartType] = useState('');

  // DOE config modal
  const [doeModalOpen, setDoeModalOpen] = useState(false);
  const [doeType, setDoeType] = useState('factorial');
  const [doeOptimize, setDoeOptimize] = useState('maximize');
  const [doeTarget, setDoeTarget] = useState<number | null>(null);
  const [doeResponseCol, setDoeResponseCol] = useState<string>('');
  const [doeFactorCols, setDoeFactorCols] = useState<string[]>([]);

  // Analysis history (session-level)
  const [analysisHistory, setAnalysisHistory] = useState<Array<{
    id: number; module: string; moduleName: string; timestamp: string;
    verdict: string; columns: string[]; cacheKey: string;
  }>>([]);
  const historyIdRef = useRef(0);

  // Module recommendations + compatibility
  const [recommendations, setRecommendations] = useState<RecommendedModule[]>([]);
  const [allModuleCompat, setAllModuleCompat] = useState<Record<string, RecommendedModule>>({});
  const [recoLoading, setRecoLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');

  // Result — 6 hooks
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [loadingHint, setLoadingHint] = useState('正在执行统计分析...');
  const [prerequisites, setPrerequisites] = useState<any[]>([]);
  const [topStatsData, setTopStatsData] = useState<any[]>([]);
  const [charts, setCharts] = useState<any[]>([]);
  const [verdict, setVerdict] = useState<{ status: string; title: string; message: string; details?: string[]; matrix?: any[] } | null>(null);
  const [explanation, setExplanation] = useState<AIExplanation | null>(null);
  const [sopAdvice, setSopAdvice] = useState<any[]>([]);
  const [dmaicPhase, setDmaicPhase] = useState('');
  const [statsTable, setStatsTable] = useState<{ columns: any[]; data: any[] }>({ columns: [], data: [] });
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [rawStats, setRawStats] = useState<any>(null);
  const [statTables, setStatTables] = useState<any[]>([]);
  const [ciItems, setCiItems] = useState<any[]>([]);
  const [anomalyPopover, setAnomalyPopover] = useState<{
    visible: boolean; x: number; y: number;
    point: number; value: number; rules: Array<{ rule: number; desc: string; cause: string }>;
  } | null>(null);

  // Interactive Cpk calculator
  const [interactiveUsl, setInteractiveUsl] = useState<number | null>(null);
  const [interactiveLsl, setInteractiveLsl] = useState<number | null>(null);

  const cacheRef = useRef<Map<string, any>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Annotation system
  const chartRefsMap = useRef<Record<number, any>>({});
  const [annotationStore, annotationDispatch] = useReducer(annotationReducer, initialAnnotationStore);
  const [annotationEnabledIdx, setAnnotationEnabledIdx] = useState<number | null>(null);

  // ESC to close DOE overlay
  useEffect(() => {
    if (!doeModalOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setDoeModalOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [doeModalOpen]);

  // ESC to close module overlay (handled inside ModuleOverlay component)

  // Annotation persistence
  useEffect(() => {
    if (!fileId) return;
    try {
      const saved = localStorage.getItem(`trendata_ann_analysis_${fileId}`);
      if (saved) annotationDispatch({ type: 'LOAD_STORE', annotations: JSON.parse(saved) });
    } catch { /* ignore */ }
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    const timer = setTimeout(() => {
      const data = annotationStore.annotations;
      if (Object.keys(data).length > 0) {
        localStorage.setItem(`trendata_ann_analysis_${fileId}`, JSON.stringify(data));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [annotationStore.annotations, fileId]);

  // Derived
  const allColNames = useMemo(() => columns.map(c => c.name), [columns]);
  // 可作为子组列的列：分类列 + 唯一值较少的数值列（如 1,2,3,...,8）
  const subgroupCandidates = useMemo(() => columns.filter(c => {
    if (['object', 'categorical', 'text'].includes(c.dtype)) return true;
    // 数值列但唯一值少（≤50），很可能是分组编号
    if (c.unique !== undefined && c.unique <= 200) return true;
    return false;
  }), [columns]);

  // Sync interactive USL/LSL when analysis results come in
  useEffect(() => {
    if (rawStats?.usl !== undefined && rawStats?.lsl !== undefined) {
      setInteractiveUsl(rawStats.usl);
      setInteractiveLsl(rawStats.lsl);
    }
  }, [rawStats?.usl, rawStats?.lsl]);

  // Real-time Cpk computation from interactive sliders
  const interactiveCpk = useMemo(() => {
    if (!rawStats || selectedModule !== 'process_capability') return null;
    const u = interactiveUsl;
    const l = interactiveLsl;
    if (u === null || l === null || u <= l) return null;

    const mean = rawStats.mean;
    const sigmaW = rawStats.std_within;
    const sigmaO = rawStats.std_overall;
    if (!sigmaW || !sigmaO || sigmaW <= 0 || sigmaO <= 0) return null;

    const cp = (u - l) / (6 * sigmaW);
    const cpu = (u - mean) / (3 * sigmaW);
    const cpl = (mean - l) / (3 * sigmaW);
    const cpk = Math.min(cpu, cpl);
    const pp = (u - l) / (6 * sigmaO);
    const ppu = (u - mean) / (3 * sigmaO);
    const ppl = (mean - l) / (3 * sigmaO);
    const ppk = Math.min(ppu, ppl);

    // Z scores for PPM
    const zUpper = (u - mean) / sigmaW;
    const zLower = (mean - l) / sigmaW;
    // Approximate normal CDF tail: use erfc approximation
    const normSf = (z: number) => {
      const t = 1 / (1 + 0.2316419 * Math.abs(z));
      const d = 0.3989422804 * Math.exp(-z * z / 2);
      const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.8212560 + t * 1.3302744))));
      return z > 0 ? p : 1 - p;
    };
    const ppmTotal = (normSf(zUpper) + normSf(zLower)) * 1e6;

    return { cp, cpk, pp, ppk, cpu, cpl, ppmTotal };
  }, [interactiveUsl, interactiveLsl, rawStats, selectedModule]);

  // ---- Load file data ----
  const loadFileData = useCallback(async (fid: string) => {
    setDataLoading(true);
    try {
      const [prevData, colData] = await Promise.all([getPreview(fid), getColumns(fid)]);
      setPreview(prevData);
      setColumns(colData);
      // Default: no columns selected — user picks in module overlay
      setSelectedCols([]);
    } catch { message.error('加载数据失败'); }
    finally { setDataLoading(false); }
  }, []);

  // ---- Fetch topology (debounced on column change) ----
  const fetchTopology = useCallback(async (fid: string, cols: string[]) => {
    setRecoLoading(true);
    try {
      const topo = await getTopology(fid, cols);
      setRecommendations(topo.recommended_modules.slice(0, 3));
      // Build compatibility map
      const compatMap: Record<string, RecommendedModule> = {};
      const allMods = (topo as any).all_modules || topo.recommended_modules;
      for (const m of allMods) {
        compatMap[m.module_name] = m;
      }
      setAllModuleCompat(compatMap);
    } catch {
      setRecommendations([
        { module_name: 'basic_stats', display_name: '基本统计', confidence: 0.95, reason: '适合基本统计分析', compatible: true },
      ]);
    } finally { setRecoLoading(false); }
  }, []);

  useEffect(() => {
    if (fileId) loadFileData(fileId);
  }, [fileId, loadFileData]);

  // Re-fetch topology when columns change (debounced)
  useEffect(() => {
    if (!fileId || selectedCols.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchTopology(fileId, selectedCols), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fileId, selectedCols, fetchTopology]);

  const handleFileUploaded = useCallback((info: any) => {
    setFileId(info.file_id);
    setFileName(info.filename);
    // Sync fileId to URL so other workspaces can pick it up via navigation
    setSearchParams({ fileId: info.file_id, fileName: info.filename }, { replace: true });
    setSelectedModule(''); setPrerequisites([]); setTopStatsData([]);
    setCharts([]); setVerdict(null); setExplanation(null); setSopAdvice([]); setStatTables([]); setCiItems([]);
    chartRefsMap.current = {};
    cacheRef.current.clear();
    if (info.sheet_names && info.sheet_names.length > 1) {
      setSheetNames(info.sheet_names);
      setSelectedSheet(info.sheet_names[0]); // default to first sheet
    } else {
      setSheetNames([]);
      setSelectedSheet('');
    }
  }, [setSearchParams]);

  // ---- Run analysis ----
  const executeAnalysis = useCallback(async (moduleName: string, extraParams?: Record<string, any>) => {
    // Allow extra params to override the columns set (used by regression which has its own X/Y picker)
    const effectiveCols: string[] = (extraParams && Array.isArray(extraParams.columns) && extraParams.columns.length > 0)
      ? (extraParams.columns as string[])
      : selectedCols;
    if (effectiveCols.length === 0) { message.warning('请至少选择一列数据'); return; }
    setSelectedModule(moduleName);

    // Build cache key from extraParams (most up-to-date) rather than stale state
    const extraStr = extraParams ? JSON.stringify(extraParams) : '';
    const cacheKey = `${moduleName}_${effectiveCols.join(',')}_${usl}_${lsl}_${extraStr}`;
    if (cacheRef.current.has(cacheKey)) {
      const c = cacheRef.current.get(cacheKey);
      setPrerequisites(c.prerequisites); setTopStatsData(c.topStats);
      setCharts(c.charts); setVerdict(c.verdict);
      setExplanation(c.explanation); setSopAdvice(c.sopAdvice);
      setDmaicPhase(c.dmaicPhase); setStatsTable(c.statsTable); setRawStats(c.rawStats);
      setStatTables(c.statTables || []); setCiItems(c.ciItems || []);
      return;
    }

    chartRefsMap.current = {};
    setAnalysisLoading(true);
    setLoadingHint('正在执行统计分析...');
    const hintTimer = setTimeout(() => setLoadingHint('AI 分析中，请耐心等待...'), 5000);

    try {
      const params: Record<string, any> = {
        columns: effectiveCols,
        missingStrategy: missingStrategy !== 'none' ? missingStrategy : undefined,
      };
      if (usl !== null) params.usl = usl;
      if (lsl !== null) params.lsl = lsl;
      if (subgroupSize > 1) params.subgroup_size = subgroupSize;
      if (subgroupCol) params.subgroup_col = subgroupCol;
      // Module-specific params
      if (extraParams) Object.assign(params, extraParams);
      if (moduleName === 'spc' && spcChartType) params.spc_chart_type = spcChartType;
      if (moduleName === 'doe') {
        params.doe_type = doeType;
        params.optimize_direction = doeOptimize;
        if (doeTarget !== null) params.target_value = doeTarget;
        if (doeResponseCol) params.response_col = doeResponseCol;
        if (doeFactorCols.length > 0) params.factor_cols = doeFactorCols;
      }
      if (selectedSheet) params.sheet_name = selectedSheet;

      // Allow extra.file_id to override stale closure fileId (needed by runTestForModule)
      const effectiveFileId = (extraParams?.file_id as string) || fileId;
      const res = await runAnalysis({ file_id: effectiveFileId, module_name: moduleName, params });

      const newState = {
        prerequisites: res.prerequisites || [],
        topStats: res.top_stats || [],
        charts: res.charts || [],
        verdict: res.verdict || res.conclusion || null,
        explanation: res.ai_explanation,
        sopAdvice: res.sop_advice || [],
        dmaicPhase: res.dmaic_phase || '',
        statsTable: buildStatsTable(res.statistics),
        rawStats: res.statistics || null,
        statTables: (res as any).tables || [],
        ciItems: (res as any).confidence_intervals || [],
      };
      setPrerequisites(newState.prerequisites); setTopStatsData(newState.topStats);
      setCharts(newState.charts); setVerdict(newState.verdict);
      setExplanation(newState.explanation); setSopAdvice(newState.sopAdvice);
      setDmaicPhase(newState.dmaicPhase); setStatsTable(newState.statsTable); setStatsExpanded(false);
      setRawStats(res.statistics || null);
      setStatTables(newState.statTables); setCiItems(newState.ciItems);
      cacheRef.current.set(cacheKey, newState);

      // Add to history
      const modInfo = ALL_MODULES.find(m => m.key === moduleName);
      historyIdRef.current += 1;
      setAnalysisHistory(prev => [{
        id: historyIdRef.current,
        module: moduleName,
        moduleName: modInfo?.name || moduleName,
        timestamp: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        verdict: newState.verdict?.status || '?',
        columns: effectiveCols.slice(0, 3),
        cacheKey,
      }, ...prev].slice(0, 20));
    } catch (err: any) {
      console.error('[trendata] analysis error:', err);
      message.error(`分析执行失败: ${err?.response?.data?.detail || err?.message || '未知错误'}`);
    }
    finally { clearTimeout(hintTimer); setAnalysisLoading(false); }
  }, [fileId, selectedCols, usl, lsl, missingStrategy, subgroupSize, subgroupCol, selectedSheet, doeResponseCol, doeFactorCols, doeType, doeOptimize, doeTarget, spcChartType]);

  // Module click handler — open overlay panel for all modules except basic_stats
  const handleModuleClick = useCallback((moduleName: string) => {
    if (moduleName === 'doe') {
      setPendingModule(moduleName);
      setDoeModalOpen(true);
      return;
    }
    // All other modules open their config overlay
    setPendingModule(moduleName);
    setActiveOverlay(moduleName);
  }, [executeAnalysis]);

  // DEV ONLY — load synthetic test data + auto-execute analysis for a module.
  // Remove this function (and the 🧪 buttons) before production.
  const runTestForModule = useCallback(async (moduleName: string) => {
    try {
      setAnalysisLoading(true);
      setLoadingHint('🧪 加载测试数据...');
      const td = await fetchTestData(moduleName);
      // Mimic upload-completed flow
      setFileId(td.file_id);
      setFileName(td.filename);
      setSearchParams({ fileId: td.file_id, fileName: td.filename }, { replace: true });
      setSheetNames([]); setSelectedSheet('');
      cacheRef.current.clear();
      // Pre-fetch columns for the right rail
      try {
        const cols = await getColumns(td.file_id);
        setColumns(cols);
      } catch {}
      // Apply suggested columns + module-specific UI state
      const sugCols = td.suggested.columns || [];
      setSelectedCols(sugCols);
      const extra: Record<string, any> = { ...(td.suggested.extra || {}) };
      if (extra.usl !== undefined) setUsl(extra.usl);
      if (extra.lsl !== undefined) setLsl(extra.lsl);
      if (moduleName === 'doe') {
        if (extra.response_col) setDoeResponseCol(extra.response_col);
        if (extra.factor_cols) setDoeFactorCols(extra.factor_cols);
        if (extra.doe_type) setDoeType(extra.doe_type);
        if (extra.optimize_direction) setDoeOptimize(extra.optimize_direction);
      }
      if (moduleName === 'spc' && extra.spc_chart_type) setSpcChartType(extra.spc_chart_type);
      // Force file_id + columns into params to bypass stale React state in executeAnalysis closure
      extra.columns = sugCols;
      extra.file_id = td.file_id;
      // Execute analysis directly (skip the modal)
      setSelectedModule(moduleName);
      await executeAnalysis(moduleName, extra);
      message.success(`🧪 ${moduleName} 测试数据已加载并分析完成`);
    } catch (err: any) {
      message.error(`🧪 测试加载失败: ${err?.response?.data?.detail || err?.message || '未知错误'}`);
    } finally {
      setAnalysisLoading(false);
    }
  }, [executeAnalysis, setSearchParams]);

  // Restore a historical analysis result from cache (show report, not modal)
  const handleHistoryClick = useCallback((h: { module: string; cacheKey: string }) => {
    const cached = cacheRef.current.get(h.cacheKey);
    if (cached) {
      setSelectedModule(h.module);
      setPrerequisites(cached.prerequisites); setTopStatsData(cached.topStats);
      setCharts(cached.charts); setVerdict(cached.verdict);
      setExplanation(cached.explanation); setSopAdvice(cached.sopAdvice);
      setDmaicPhase(cached.dmaicPhase); setStatsTable(cached.statsTable); setRawStats(cached.rawStats);
      setStatTables(cached.statTables || []); setCiItems(cached.ciItems || []);
    } else {
      // Cache expired — fall back to opening the method selector
      handleModuleClick(h.module);
    }
  }, [handleModuleClick]);

  const handleRerun = useCallback(() => {
    if (selectedModule) {
      // Clear ALL cache entries for this module to avoid stale results
      for (const k of Array.from(cacheRef.current.keys())) {
        if (k.startsWith(selectedModule + '_')) cacheRef.current.delete(k);
      }
      executeAnalysis(selectedModule);
    }
  }, [selectedModule, executeAnalysis]);

  const currentModuleInfo = ALL_MODULES.find(m => m.key === selectedModule);
  const hasResult = !analysisLoading && selectedModule && (charts.length > 0 || topStatsData.length > 0 || prerequisites.length > 0);

  // ========== Phase 1: Upload ==========
  if (!fileId) {
    return (
      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 300px', gap: 32, alignItems: 'start',
        }}>
          {/* Left: Upload */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase',
              color: '#533afd', marginBottom: 12,
            }}>
              Step 1 / 4 · 上传数据
            </div>
            <h2 style={{
              fontSize: 22, fontWeight: 700, color: 'var(--title-color)',
              margin: '0 0 8px', lineHeight: 1.3,
            }}>
              上传数据文件
            </h2>
            <p style={{
              fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px', lineHeight: 1.6,
            }}>
              支持 CSV、TSV、XLSX 格式，最大 25MB。上传后 AI 自动分析数据结构并推荐分析方法。
            </p>
            <FileUpload onUploadSuccess={handleFileUploaded} />
            <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)' }}>
              没有数据？
              <span
                style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 500, marginLeft: 4 }}
                onClick={async () => {
                  try {
                    const resp = await fetch('/test_data/test_basic.csv');
                    if (!resp.ok) throw new Error('fetch failed');
                    const blob = await resp.blob();
                    const file = new File([blob], 'demo_basic.csv', { type: 'text/csv' });
                    const { uploadFile: doUpload } = await import('../services/api');
                    const info = await doUpload(file);
                    handleFileUploaded(info);
                  } catch {
                    message.info('请从 test_data/ 目录手动上传示例文件');
                  }
                }}
              >
                试用示例数据
              </span>
            </div>
          </div>
          {/* Right: Stepper */}
          <div style={{
            background: isDark ? '#0f1629' : '#061b31',
            borderRadius: 8, padding: '24px 20px',
            color: '#fff',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>分析流程</div>
            {[
              { n: '1', label: '上传数据', desc: 'CSV / Excel 文件', active: true },
              { n: '2', label: '选择列', desc: '指定分析变量', active: false },
              { n: '3', label: '选择方法', desc: 'AI 推荐 Top 3', active: false },
              { n: '4', label: '查看报告', desc: '图表 + AI 解读', active: false },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                marginBottom: i < 3 ? 16 : 0,
                opacity: s.active ? 1 : 0.4,
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                  background: s.active ? '#533afd' : 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                }}>{s.n}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{s.desc}</div>
                </div>
              </div>
            ))}
            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              fontSize: 11, color: 'rgba(255,255,255,0.4)',
            }}>
              12 个分析模块 · DMAIC 全覆盖
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== Phase 2: Workspace ==========
  return (
    <div style={{ display: 'flex', gap: 12, minHeight: 'calc(100vh - 80px)', overflow: 'hidden', maxWidth: '100%' }}>
      {/* ======== LEFT: Data & Config ======== */}
      <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* File info */}
        <div className="card-trendata" style={{ padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--title-color)', fontSize: 13 }}>数据文件</span>
            <Button size="small" type="link" onClick={() => { setFileId(''); setFileName(''); }}>更换</Button>
          </div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            {fileName || fileId}
            {preview && <span style={{ marginLeft: 4 }}><Tag color="blue" style={{ fontSize: 10 }}>{preview.total_rows}行</Tag><Tag color="cyan" style={{ fontSize: 10 }}>{preview.columns.length}列</Tag></span>}
          </div>
          {sheetNames.length > 1 && (
            <div style={{ marginTop: 6, overflow: 'hidden' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Sheet:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {sheetNames.map(s => (
                  <button
                    key={s}
                    onClick={async () => {
                      setSelectedSheet(s);
                      try {
                        const API_URL = API_BASE_URL;
                        const res = await fetch(`${API_URL}/api/upload/reparse?file_id=${fileId}&sheet_name=${encodeURIComponent(s)}`, { method: 'POST' });
                        if (res.ok) {
                          const previewRes = await getPreview(fileId);
                          setPreview(previewRes);
                          const colsRes = await getColumns(fileId);
                          setColumns(colsRes);
                          setSelectedCols(colsRes.filter(c => ['float64','int64','float','integer','number'].includes(c.dtype)).map(c => c.name));
                        }
                      } catch {}
                    }}
                    style={{
                      padding: '3px 10px', fontSize: 11, fontWeight: s === selectedSheet ? 600 : 400,
                      border: `1px solid ${s === selectedSheet ? 'var(--primary)' : 'var(--border-color)'}`,
                      borderRadius: 4, cursor: 'pointer',
                      background: s === selectedSheet ? 'rgba(83,58,253,0.08)' : 'transparent',
                      color: s === selectedSheet ? 'var(--primary)' : 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Data preview — scrollable to show all rows */}
        <div className="card-trendata" style={{ padding: 12, maxHeight: 360, overflowX: 'auto', overflowY: 'auto' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--title-color)', marginBottom: 6 }}>
            数据预览 {preview && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 10 }}>({preview.data.length}/{preview.total_rows} 行)</span>}
          </div>
          {preview && (
            <table style={{ minWidth: '100%', width: 'max-content', fontSize: 10, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '3px 4px', borderBottom: '1px solid #e8e8e8', color: '#999', position: 'sticky', top: 0, background: 'var(--card-bg)', zIndex: 1, fontSize: 9 }}>#</th>
                  {preview.columns.map(c => (
                    <th key={c} style={{
                      padding: '3px 4px', borderBottom: '1px solid #e8e8e8', textAlign: 'left',
                      background: selectedCols.includes(c) ? '#e6f7ff' : 'var(--card-bg)',
                      color: selectedCols.includes(c) ? '#1890ff' : '#666',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      position: 'sticky', top: 0, zIndex: 1,
                    }}
                    onClick={() => {
                      if (selectedCols.includes(c)) setSelectedCols(prev => prev.filter(x => x !== c));
                      else setSelectedCols(prev => [...prev, c]);
                    }}
                    title={c}
                    >
                      {c.length > 12 ? c.slice(0, 12) + '…' : c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.data.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: '2px 4px', borderBottom: '1px solid #f0f0f0', color: '#bbb', fontSize: 9 }}>{i + 1}</td>
                    {preview!.columns.map(c => (
                      <td key={c} style={{ padding: '2px 4px', borderBottom: '1px solid #f0f0f0', color: 'var(--text-primary)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontFeatureSettings: '"tnum"',
                      }}>
                        {row[c] !== null && row[c] !== undefined ? String(row[c]).slice(0, 12) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Column selection */}
        <div className="card-trendata" style={{ padding: 12, flex: 1, overflow: 'auto' }}>
          <Divider orientation="left" style={{ fontSize: 12, margin: '0 0 6px', fontWeight: 600, color: 'var(--title-color)' }}>分析列 ({selectedCols.length}/{allColNames.length})</Divider>
          <div style={{ marginBottom: 4 }}>
            <Checkbox
              checked={selectedCols.length === allColNames.length && allColNames.length > 0}
              indeterminate={selectedCols.length > 0 && selectedCols.length < allColNames.length}
              onChange={e => setSelectedCols(e.target.checked ? [...allColNames] : [])}
              style={{ fontSize: 11 }}
            >全选</Checkbox>
          </div>
          {dataLoading ? <Spin size="small" /> : (
            <Checkbox.Group value={selectedCols} onChange={vals => setSelectedCols(vals as string[])}
              style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {columns.map(col => (
                <Checkbox key={col.name} value={col.name} style={{ fontSize: 11 }}>
                  {col.name} <Tag color={TYPE_TAG_COLORS[col.dtype] || 'default'} style={{ fontSize: 9 }}>{col.dtype}</Tag>
                </Checkbox>
              ))}
            </Checkbox.Group>
          )}

          {/* Subgroup config */}
          <Divider orientation="left" style={{ fontSize: 12, margin: '12px 0 8px', fontWeight: 600, color: 'var(--title-color)' }}>子组设置</Divider>
          <Select value={subgroupMode} onChange={(v) => { setSubgroupMode(v); if (v === 'none') { setSubgroupSize(1); setSubgroupCol(''); }}} size="small" style={{ width: '100%', marginBottom: 6 }}
            options={[
              { value: 'none', label: '无子组 (n=1)' },
              { value: 'size', label: '指定子组大小' },
              { value: 'column', label: '选择子组列' },
            ]}
          />
          {subgroupMode === 'size' && (
            <InputNumber value={subgroupSize} onChange={v => setSubgroupSize(v || 1)} min={1} max={25} size="small" style={{ width: '100%' }} addonAfter="个/组" />
          )}
          {subgroupMode === 'column' && (
            <Select value={subgroupCol} onChange={setSubgroupCol} size="small" style={{ width: '100%' }} placeholder="选择子组列"
              options={subgroupCandidates.map(c => ({ value: c.name, label: `${c.name} (${c.unique || '?'}组)` }))}
            />
          )}

          {/* Missing value */}
          <Divider orientation="left" style={{ fontSize: 12, margin: '12px 0 8px', fontWeight: 600, color: 'var(--title-color)' }}>缺失值</Divider>
          <Select value={missingStrategy} onChange={setMissingStrategy} options={MISSING_STRATEGIES} style={{ width: '100%' }} size="small" />

          {selectedModule && (
            <Button type="primary" icon={<ReloadOutlined />} block style={{ marginTop: 12 }} size="small" onClick={handleRerun}>重新分析</Button>
          )}

          {/* Analysis History */}
          {analysisHistory.length > 0 && (
            <>
              <Divider orientation="left" style={{ fontSize: 12, margin: '12px 0 8px', fontWeight: 600, color: 'var(--title-color)' }}>
                分析历史 ({analysisHistory.length})
              </Divider>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {analysisHistory.map(h => (
                  <div key={h.id} onClick={() => handleHistoryClick(h)}
                    style={{
                      padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                      background: selectedModule === h.module ? '#e6f7ff' : '#fafafa',
                      border: `1px solid ${selectedModule === h.module ? '#91d5ff' : '#f0f0f0'}`,
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 500 }}>{h.moduleName}</span>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                        background: h.verdict === 'good' ? '#52c41a' : h.verdict === 'warning' ? '#faad14' : h.verdict === 'bad' ? '#f5222d' : '#d9d9d9',
                      }} />
                    </div>
                    <div style={{ color: '#999', fontSize: 10 }}>{h.timestamp} · {h.columns.join(', ')}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ======== CENTER: Report ======== */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, overflow: 'hidden' }}>
        {analysisLoading && (
          <div style={{ padding: '24px' }}>
            {/* Skeleton placeholders */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{
                  height: 64, borderRadius: 6,
                  background: 'var(--bg-subtle)', border: '1px solid var(--border-color)',
                  animation: 'fadeIn 1s ease infinite alternate',
                }} />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[1,2].map(i => (
                <div key={i} style={{
                  height: 240, borderRadius: 6,
                  background: 'var(--bg-subtle)', border: '1px solid var(--border-color)',
                  animation: 'fadeIn 1s ease infinite alternate',
                  animationDelay: `${i * 0.2}s`,
                }} />
              ))}
            </div>
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              <Spin size="small" style={{ marginRight: 8 }} />{loadingHint}
            </div>
          </div>
        )}

        {!analysisLoading && !hasResult && (
          <div style={{ padding: '40px 24px' }}>
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase',
              color: 'var(--primary)', marginBottom: 12,
            }}>
              {selectedCols.length > 0 ? 'Step 3 / 4 · 选择方法' : 'Step 2 / 4 · 选择列'}
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--title-color)', margin: '0 0 8px' }}>
              {selectedCols.length > 0 ? '从右侧选择分析方法' : '先勾选左侧的分析列'}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px', lineHeight: 1.6 }}>
              {selectedCols.length > 0
                ? `已选 ${selectedCols.length} 列。点击右侧模块卡片开始分析，AI 已根据数据特征推荐了 Top 3。`
                : '勾选要分析的数据列，系统将自动推荐最合适的分析方法。'}
            </p>
            {selectedCols.length > 0 && recommendations.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {recommendations.slice(0, 3).map(rec => {
                  const mod = ALL_MODULES.find(m => m.key === rec.module_name);
                  return (
                    <button key={rec.module_name} onClick={() => handleModuleClick(rec.module_name)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 16px', fontSize: 13, fontWeight: 600,
                        background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                        borderRadius: 6, cursor: 'pointer', color: mod?.color || 'var(--primary)',
                      }}>
                      {mod?.icon} {mod?.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {hasResult && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20, color: currentModuleInfo?.color }}>{currentModuleInfo?.icon}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--title-color)' }}>{currentModuleInfo?.name}</span>
                {dmaicPhase && <Tag color="purple">{dmaicPhase}</Tag>}
                <Tag color="green">分析完成</Tag>
              </div>
              <Button icon={<FileTextOutlined />} type="primary" onClick={async () => {
                try {
                  const params: Record<string, any> = { columns: selectedCols };
                  if (usl !== null) params.usl = usl;
                  if (lsl !== null) params.lsl = lsl;
                  if (subgroupSize > 1) params.subgroup_size = subgroupSize;
                  if (subgroupCol) params.subgroup_col = subgroupCol;
                  if (selectedModule === 'spc' && spcChartType) params.spc_chart_type = spcChartType;
                  if (selectedModule === 'doe') { params.doe_type = doeType; params.optimize_direction = doeOptimize; }
                  const resp = await fetch(`${API_BASE_URL}/api/analyze/export`, {
                    method: 'POST', headers: {'Content-Type':'application/json'},
                    body: JSON.stringify({ file_id: fileId, module_name: selectedModule, params }),
                  });
                  if (!resp.ok) throw new Error('Export failed');
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const ts = new Date().toISOString().slice(0,16).replace(/[-:T]/g,'');
                  a.href = url; a.download = `trendata_${selectedModule}_${ts}.html`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  message.success('HTML 报告已下载');
                } catch { message.error('导出失败，请确认后端服务'); }
              }}>导出报告</Button>
            </div>

            <PrerequisiteCard checks={prerequisites} />

            {topStatsData.length > 0 && (
              <TopStats items={topStatsData.map(s => ({
                label: s.label,
                value: typeof s.value === 'number' ? (Number.isFinite(s.value) ? s.value : 0) : s.value,
                suffix: s.suffix,
                status: s.status,
              }))} />
            )}

            {charts.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: charts.length > 2 ? 'repeat(2, 1fr)' : '1fr', gap: 12 }}>
                {charts.map((chart: any, i: number) => {
                  // Inject toolbox + dataZoom into every chart
                  const isMultiGrid = chart.option?.grid && Array.isArray(chart.option.grid);
                  const is3D = !!chart.option?.grid3D;
                  const enhanced = {
                    ...chart.option,
                    toolbox: {
                      show: true, right: 8, top: 4,
                      iconStyle: { borderColor: '#999' },
                      feature: {
                        saveAsImage: { pixelRatio: 2, title: '保存图片' },
                        dataZoom: { title: { zoom: '框选缩放', back: '还原' } },
                        restore: { title: '还原' },
                      },
                    },
                    // Add slider dataZoom for single-grid line/bar charts (not multi-grid or 3D)
                    ...(!isMultiGrid && !is3D && !chart.option?.dataZoom ? {
                      dataZoom: [
                        { type: 'inside', throttle: 50 },
                        { type: 'slider', bottom: 2, height: 18, borderColor: 'transparent',
                          fillerColor: 'rgba(102,126,234,0.15)', handleSize: '60%' },
                      ],
                    } : {}),
                  };
                  injectDarkMode(enhanced, isDark);
                  injectAxisRange(enhanced);
                  const chartHeight = isMultiGrid ? 430 : is3D ? 460 : 340;
                  return (
                    <div key={i} className="card-trendata" style={{ padding: '12px 12px 4px', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>{chart.title}</h4>
                        <Tooltip title={annotationEnabledIdx === i ? '关闭标注' : '标注'}>
                          <Button size="small" shape="circle" icon={<HighlightOutlined />}
                            onClick={() => setAnnotationEnabledIdx(prev => prev === i ? null : i)}
                            style={{
                              border: 'none', background: annotationEnabledIdx === i ? '#533afd' : 'rgba(0,0,0,0.04)',
                              color: annotationEnabledIdx === i ? '#fff' : '#666', fontSize: 12,
                            }} />
                        </Tooltip>
                      </div>
                      <div style={{ overflow: 'hidden', borderRadius: 8, position: 'relative' }}>
                        <ReactEChartsCore
                          ref={(el: any) => { if (el) chartRefsMap.current[i] = el; }}
                          option={enhanced}
                          style={{ height: chartHeight, width: '100%' }}
                          notMerge
                          onEvents={selectedModule === 'spc' ? {
                            click: (params: any) => {
                              if (params.componentType === 'markPoint' && params.data?.rules) {
                                setAnomalyPopover({
                                  visible: true,
                                  x: params.event?.offsetX || 0,
                                  y: params.event?.offsetY || 0,
                                  point: params.data.xAxis + 1,
                                  value: params.data.yAxis,
                                  rules: params.data.rules,
                                });
                              }
                            },
                          } : undefined}
                        />
                        <AnnotationCanvas
                          chartRef={{ current: chartRefsMap.current[i] || null }}
                          chartId={`analysis-${selectedModule}-${i}`}
                          store={annotationStore}
                          dispatch={annotationDispatch}
                          enabled={annotationEnabledIdx === i}
                        />
                      </div>
                      {annotationEnabledIdx === i && (
                        <AnnotationToolbar
                          store={annotationStore}
                          dispatch={annotationDispatch}
                          chartId={`analysis-${selectedModule}-${i}`}
                          chartRef={{ current: chartRefsMap.current[i] || null }}
                        />
                      )}
                      {anomalyPopover?.visible && (
                        <div
                          style={{
                            position: 'absolute', zIndex: 100,
                            left: Math.max(8, Math.min(anomalyPopover.x - 40, 380)),
                            top: Math.max(8, anomalyPopover.y - 10),
                            background: isDark ? '#2a2a2a' : '#fff',
                            borderRadius: 10, padding: '14px 16px',
                            boxShadow: isDark ? '0 6px 24px rgba(0,0,0,0.5)' : '0 6px 24px rgba(0,0,0,0.15)',
                            border: `1px solid ${isDark ? '#444' : '#e8e8e8'}`,
                            maxWidth: 340, fontSize: 12,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div style={{ fontWeight: 600, color: '#f5222d', fontSize: 13 }}>
                              异常点 #{anomalyPopover.point}
                              <span style={{ fontWeight: 400, color: isDark ? '#ccc' : '#666', marginLeft: 8 }}>
                                观测值: {anomalyPopover.value}
                              </span>
                            </div>
                            <Button type="text" size="small" icon={<CloseOutlined />}
                              onClick={() => setAnomalyPopover(null)}
                              style={{ fontSize: 10, width: 20, height: 20, minWidth: 20, color: isDark ? '#999' : '#bbb' }} />
                          </div>
                          {anomalyPopover.rules.map((r, ri) => (
                            <div key={ri} style={{
                              marginBottom: 6, padding: '6px 8px', borderRadius: 6,
                              background: isDark ? 'rgba(245,34,45,0.08)' : 'rgba(245,34,45,0.04)',
                              border: `1px solid ${isDark ? 'rgba(245,34,45,0.2)' : 'rgba(245,34,45,0.1)'}`,
                            }}>
                              <div style={{ fontWeight: 600, color: isDark ? '#a0b4ff' : '#061b31', fontSize: 12 }}>
                                Nelson 规则 {r.rule}
                              </div>
                              <div style={{ color: isDark ? '#ddd' : '#333', marginTop: 2, lineHeight: 1.5 }}>{r.desc}</div>
                              {r.cause && (
                                <div style={{ color: isDark ? '#f5a623' : '#d48806', marginTop: 3, fontSize: 11, lineHeight: 1.4 }}>
                                  {r.cause}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Structured statistical tables */}
            {statTables.length > 0 && statTables.map((tbl: any, i: number) => (
              <StatTable
                key={`stat-tbl-${i}`}
                title={tbl.title}
                columns={tbl.columns}
                rows={tbl.rows}
                footnotes={tbl.footnotes}
              />
            ))}

            {/* Confidence interval visualization */}
            {ciItems.length > 0 && (
              <ConfidenceIntervalChart title="置信区间" items={ciItems} />
            )}

            {/* Interactive Cpk Calculator */}
            {selectedModule === 'process_capability' && rawStats && interactiveCpk && isFinite(rawStats.mean) && isFinite(rawStats.usl) && isFinite(rawStats.lsl) && rawStats.usl > rawStats.lsl && (
              <div className="card-trendata" style={{ padding: 16 }}>
                <div className="title-bar" style={{ marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>
                    规格限模拟器 — 拖拽查看 Cpk 变化
                  </h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* USL Slider */}
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      USL (规格上限): <span style={{ fontWeight: 600, color: '#df1b41' }}>{interactiveUsl?.toFixed(3)}</span>
                    </div>
                    <Slider
                      value={interactiveUsl ?? rawStats.usl}
                      min={rawStats.mean}
                      max={rawStats.mean + (rawStats.usl - rawStats.mean) * 3}
                      step={(rawStats.usl - rawStats.lsl) / 200}
                      onChange={(v: number) => setInteractiveUsl(v)}
                      tooltip={{ formatter: (v) => v?.toFixed(3) }}
                      styles={{ track: { background: '#df1b41' }, rail: { background: isDark ? '#333' : '#e8e8e8' } }}
                    />
                  </div>
                  {/* LSL Slider */}
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      LSL (规格下限): <span style={{ fontWeight: 600, color: '#df1b41' }}>{interactiveLsl?.toFixed(3)}</span>
                    </div>
                    <Slider
                      value={interactiveLsl ?? rawStats.lsl}
                      min={rawStats.mean - (rawStats.mean - rawStats.lsl) * 3}
                      max={rawStats.mean}
                      step={(rawStats.usl - rawStats.lsl) / 200}
                      onChange={(v: number) => setInteractiveLsl(v)}
                      tooltip={{ formatter: (v) => v?.toFixed(3) }}
                      styles={{ track: { background: '#df1b41' }, rail: { background: isDark ? '#333' : '#e8e8e8' } }}
                    />
                  </div>
                </div>

                {/* Real-time Cpk display */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 12,
                  padding: 12, borderRadius: 8,
                  background: isDark ? 'rgba(102,126,234,0.08)' : 'rgba(102,126,234,0.04)',
                  border: `1px solid ${isDark ? 'rgba(102,126,234,0.2)' : 'rgba(102,126,234,0.1)'}`,
                }}>
                  {[
                    { label: 'Cpk', value: interactiveCpk.cpk, original: rawStats.cpk },
                    { label: 'Cp', value: interactiveCpk.cp, original: rawStats.cp },
                    { label: 'Ppk', value: interactiveCpk.ppk, original: rawStats.ppk },
                    { label: 'PPM', value: interactiveCpk.ppmTotal, original: rawStats.ppm_total, isInt: true },
                  ].map(({ label, value, original, isInt }) => {
                    const diff = value - original;
                    const isCpkLike = label !== 'PPM';
                    const improved = isCpkLike ? diff > 0.001 : diff < -0.5;
                    const worsened = isCpkLike ? diff < -0.001 : diff > 0.5;
                    return (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
                        <div style={{
                          fontSize: 20, fontWeight: 700, marginTop: 2,
                          color: improved ? '#52c41a' : worsened ? '#f5222d' : 'var(--text-primary)',
                        }}>
                          {isFinite(value) ? (isInt ? Math.round(value).toLocaleString() : value.toFixed(3)) : '—'}
                        </div>
                        {isFinite(diff) && Math.abs(diff) > (isInt ? 0.5 : 0.001) && (
                          <div style={{ fontSize: 10, color: improved ? '#52c41a' : '#f5222d', marginTop: 2 }}>
                            {diff > 0 ? '+' : ''}{isFinite(diff) ? (isInt ? Math.round(diff).toLocaleString() : diff.toFixed(3)) : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Reset button */}
                {(interactiveUsl !== rawStats.usl || interactiveLsl !== rawStats.lsl) && (
                  <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <Button size="small" onClick={() => {
                      setInteractiveUsl(rawStats.usl);
                      setInteractiveLsl(rawStats.lsl);
                    }}>重置为原始规格限</Button>
                    <Button size="small" type="primary" style={{ marginLeft: 8 }} onClick={() => {
                      if (interactiveUsl !== null) setUsl(interactiveUsl);
                      if (interactiveLsl !== null) setLsl(interactiveLsl);
                      message.success('已更新规格限，点击"重新分析"生成新报告');
                    }}>应用到分析</Button>
                  </div>
                )}
              </div>
            )}

            {/* DOE Factor Explorer */}
            {selectedModule === 'doe' && rawStats?.prediction_info && (() => {
              const pi = rawStats.prediction_info;
              const factors = pi.factors || [];
              if (factors.length === 0) return null;

              // Use local state via a wrapper - we store slider values in a ref-like approach
              // Since we can't use hooks conditionally, we use a self-contained component pattern
              return <DoeFactorExplorer predictionInfo={pi} isDark={isDark} />;
            })()}

            {/* Equation */}
            {rawStats?.equation && (
              <div className="card-trendata" style={{ padding: 12 }}>
                <div className="title-bar" style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>回归方程</h4>
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono, "Source Code Pro", "SF Mono", monospace)',
                  fontSize: 15, fontWeight: 600,
                  padding: '12px 16px', borderRadius: 8,
                  background: isDark ? 'rgba(83,58,253,0.12)' : 'rgba(83,58,253,0.06)',
                  color: 'var(--text-primary)',
                  border: `1px solid ${isDark ? 'rgba(83,58,253,0.3)' : 'rgba(83,58,253,0.18)'}`,
                  overflowX: 'auto', whiteSpace: 'nowrap',
                  fontFeatureSettings: '"tnum"',
                }}>
                  {rawStats.equation}
                </div>
                {rawStats.screened_model && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>筛选模型: {rawStats.screened_model}</div>
                )}
                {rawStats.significant_factors && (
                  <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>显著因子: {rawStats.significant_factors.join(', ')}</div>
                )}
              </div>
            )}

            {/* ANOVA Table */}
            {(rawStats?.anova_table || rawStats?.regression_anova) && (
              <div className="card-trendata" style={{ padding: 12 }}>
                <div className="title-bar" style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>方差分析表 (ANOVA)</h4>
                </div>
                <Table
                  columns={[
                    { title: '来源', dataIndex: 'source', key: 'source', width: 180 },
                    { title: 'SS', dataIndex: 'ss', key: 'ss', render: (v: any) => v !== undefined && v !== null ? Number(v).toFixed(4) : '' },
                    { title: 'df', dataIndex: 'df', key: 'df' },
                    { title: 'MS', dataIndex: 'ms', key: 'ms', render: (v: any) => v !== undefined && v !== null ? Number(v).toFixed(4) : '' },
                    { title: 'F', dataIndex: 'f', key: 'f', render: (v: any) => v !== undefined && v !== null ? Number(v).toFixed(4) : '' },
                    { title: 'P 值', dataIndex: 'p', key: 'p', render: (v: any) => v !== undefined && v !== null ? (Number(v) < 0.001 ? '<0.001' : Number(v).toFixed(4)) : '' },
                  ]}
                  dataSource={(rawStats.anova_table || rawStats.regression_anova).map((r: any, i: number) => ({ ...r, key: i }))}
                  pagination={false} size="small" bordered
                  rowClassName={(r: any) => r.significant ? 'conclusion-good' : ''}
                />
              </div>
            )}

            {/* Coefficient Table */}
            {rawStats?.coef_table && rawStats.coef_table.length > 0 && (
              <div className="card-trendata" style={{ padding: 12 }}>
                <div className="title-bar" style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>回归系数</h4>
                </div>
                <Table
                  columns={[
                    { title: '项', dataIndex: 'term', key: 'term' },
                    { title: '系数', dataIndex: 'coef', key: 'coef', render: (v: any) => Number(v).toFixed(4) },
                    { title: '标准误', dataIndex: 'std_err', key: 'std_err', render: (v: any) => Number(v).toFixed(4) },
                    { title: 't 值', dataIndex: 't', key: 't', render: (v: any) => Number(v).toFixed(4) },
                    { title: 'P 值', dataIndex: 'p', key: 'p', render: (v: any) => Number(v) < 0.001 ? '<0.001' : Number(v).toFixed(4) },
                    { title: '显著', dataIndex: 'significant', key: 'sig', render: (v: boolean) => v ? '✓' : '' },
                  ]}
                  dataSource={rawStats.coef_table.map((r: any, i: number) => ({ ...r, key: i }))}
                  pagination={false} size="small" bordered
                />
              </div>
            )}

            {statsTable.data.length > 0 && (
              <div className="card-trendata" style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--title-color)', margin: 0 }}>统计数据</h4>
                  {statsTable.data.length > 8 && (
                    <Button type="link" size="small" onClick={() => setStatsExpanded(prev => !prev)}
                      style={{ fontSize: 12, padding: 0 }}>
                      {statsExpanded ? '收起' : `展开全部 ${statsTable.data.length} 项`}
                    </Button>
                  )}
                </div>
                <Table
                  columns={statsTable.columns}
                  dataSource={statsExpanded || statsTable.data.length <= 8 ? statsTable.data : statsTable.data.slice(0, 8)}
                  pagination={false} size="small" bordered
                />
              </div>
            )}

            {verdict && <VerdictCard status={(verdict.status as 'good' | 'warning' | 'bad') || 'good'} title={verdict.title || '分析完成'} message={verdict.message || ''} details={verdict.details} matrix={verdict.matrix} />}

            {explanation && <div className="card-trendata" style={{ padding: 0 }}><AIPanel explanation={explanation} /></div>}

            <SOPAdvice items={sopAdvice} dmaicPhase={dmaicPhase} onModuleJump={handleModuleClick} />
          </>
        )}
      </div>

      {/* ======== RIGHT: Method Selection ======== */}
      <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* AI Top 3 */}
        <div className="card-trendata" style={{ padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--title-color)' }}>AI 推荐</h4>
            <Tag color="purple" style={{ fontSize: 10 }}>Top 3</Tag>
          </div>
          {recoLoading ? <Spin size="small" /> : recommendations.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
              {selectedCols.length === 0 ? '请先勾选分析列' : '加载推荐中...'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recommendations.map((rec, idx) => {
                const mod = ALL_MODULES.find(m => m.key === rec.module_name);
                const isActive = selectedModule === rec.module_name;
                return (
                  <div key={rec.module_name} onClick={() => handleModuleClick(rec.module_name)}
                    style={{
                      padding: 8, borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${isActive ? '#533afd' : '#e8e8e8'}`,
                      background: isActive ? 'rgba(102,126,234,0.08)' : '#fff',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = '#533afd'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = '#e8e8e8'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 16, color: mod?.color }}>{mod?.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--title-color)' }}>{rec.display_name}</div>
                        <div style={{ fontSize: 10, color: '#888', lineHeight: 1.3 }}>{rec.reason}</div>
                      </div>
                      <Tag color="blue" style={{ fontSize: 10 }}>{Math.round(rec.confidence * 100)}%</Tag>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* All 12 modules with compatibility */}
        <div className="card-trendata" style={{ padding: 12, flex: 1, overflow: 'auto' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--title-color)', marginBottom: 8 }}>全部模块</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ALL_MODULES.map(mod => {
              const isActive = selectedModule === mod.key;
              const compat = allModuleCompat[mod.key];
              const isCompatible = compat?.compatible !== false;
              const confidence = compat?.confidence || 0;

              return (
                <Tooltip key={mod.key} title={compat?.reason || ''} placement="left">
                  <div
                    style={{
                      padding: '8px 10px', borderRadius: 6,
                      display: 'flex', alignItems: 'center', gap: 8,
                      border: `1px solid ${isActive ? mod.color : 'var(--border-color)'}`,
                      background: isActive ? `${mod.color}10` : 'var(--card-bg)',
                      opacity: isCompatible ? 1 : 0.4,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = mod.color; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                  >
                    <div onClick={() => handleModuleClick(mod.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', minWidth: 0 }}>
                      <div style={{ fontSize: 16, color: isActive ? mod.color : 'var(--text-muted)', flexShrink: 0 }}>{mod.icon}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? mod.color : 'var(--text-primary)', lineHeight: 1.3 }}>{mod.name}</div>
                        {!isCompatible && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>不适用</div>}
                      </div>
                    </div>
                    {/* DEV ONLY — synthetic-data test button. Remove before prod. */}
                    <Tooltip title={`🧪 加载 ${mod.name} 测试数据并自动分析`} placement="top">
                      <span
                        onClick={(e) => { e.stopPropagation(); runTestForModule(mod.key); }}
                        style={{
                          fontSize: 14, lineHeight: 1, padding: '4px 6px', borderRadius: 4,
                          cursor: 'pointer', flexShrink: 0,
                          background: isDark ? 'rgba(255,193,7,0.12)' : 'rgba(255,193,7,0.15)',
                          border: `1px solid ${isDark ? 'rgba(255,193,7,0.3)' : 'rgba(255,193,7,0.35)'}`,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.background = 'rgba(255,193,7,0.3)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.background = isDark ? 'rgba(255,193,7,0.12)' : 'rgba(255,193,7,0.15)'; }}
                      >🧪</span>
                    </Tooltip>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>

      {/* ======== Unified Module Overlay (all modules except DOE & basic_stats) ======== */}
      {activeOverlay && MODULE_CONFIGS[activeOverlay] && (
        <ModuleOverlay
          moduleName={activeOverlay}
          isDark={isDark}
          onClose={() => setActiveOverlay(null)}
          onExecute={(_subType, extraParams) => {
            setActiveOverlay(null);
            // Clear cache for this module
            for (const k of Array.from(cacheRef.current.keys())) {
              if (k.startsWith(activeOverlay + '_')) cacheRef.current.delete(k);
            }
            if (pendingModule) executeAnalysis(pendingModule, extraParams);
          }}
          columns={columns}
          selectedCols={selectedCols}
          onSelectedColsChange={setSelectedCols}
          usl={usl}
          lsl={lsl}
          onUslChange={setUsl}
          onLslChange={setLsl}
          spcChartType={spcChartType}
          onSpcChartTypeChange={setSpcChartType}
        />
      )}

      {/* ======== DOE Panel Overlay ======== */}
      {doeModalOpen && (<>
        {/* Dim backdrop */}
        <div onClick={() => setDoeModalOpen(false)} style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.12)',
          animation: 'fadeIn 0.15s ease',
        }} />
        {/* Floating panel */}
        <div style={{
          position: 'fixed', zIndex: 1000,
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 720, maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 60px)',
          overflowY: 'auto',
          background: isDark ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 18, padding: '24px 28px 20px',
          boxShadow: isDark
            ? '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)'
            : '0 24px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
          animation: 'modalIn 0.2s ease',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--title-color)', margin: 0 }}>DOE 实验设计</h2>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>选择设计类型，配置后开始分析</p>
            </div>
            <Button type="text" icon={<CloseOutlined />} onClick={() => setDoeModalOpen(false)}
              style={{ width: 32, height: 32, minWidth: 32, borderRadius: 8, color: 'var(--text-muted)' }} />
          </div>

          {/* DOE Type Cards - compact grid */}
          {[
            { group: '筛选设计', color: '#533afd', items: [
              { key: 'factorial', name: '全因子/部分因子', desc: '主效应+交互+分辨率', icon: '2\u207A' },
              { key: 'pb', name: 'Plackett-Burman', desc: '6+因子快速筛选', icon: 'PB' },
              { key: 'dsd', name: '确定性筛选 DSD', desc: '主效应+弯曲，最少运行', icon: 'DS' },
            ]},
            { group: '优化设计', color: '#f093fb', items: [
              { key: 'rsm', name: 'RSM 中心复合', desc: '二次模型+3D曲面+等高线', icon: '3D' },
              { key: 'box_behnken', name: 'Box-Behnken', desc: 'RSM无极端角点', icon: 'BB' },
            ]},
            { group: '稳健 & 特殊', color: '#43e97b', items: [
              { key: 'taguchi', name: '田口方法', desc: 'S/N比+稳健优化', icon: 'SN' },
              { key: 'mixture', name: '混料设计', desc: '配方优化(和=常数)', icon: 'Mx' },
            ]},
          ].map(group => (
            <div key={group.group} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: group.color, letterSpacing: 0.8,
                textTransform: 'uppercase', marginBottom: 6 }}>{group.group}</div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(group.items.length, 3)}, 1fr)`, gap: 8 }}>
                {group.items.map(item => {
                  const sel = doeType === item.key;
                  return (
                    <div key={item.key} onClick={() => setDoeType(item.key)}
                      style={{
                        padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: sel
                          ? (isDark ? `${group.color}20` : `${group.color}0c`)
                          : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                        border: `1.5px solid ${sel ? group.color : 'transparent'}`,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'; }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: sel ? group.color : (isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'),
                        color: sel ? '#fff' : group.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, fontFamily: 'monospace',
                        transition: 'all 0.15s',
                      }}>{item.icon}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: sel ? group.color : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{item.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Divider */}
          <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', margin: '14px 0' }} />

          {/* Config row */}
          <div style={{ display: 'grid', gridTemplateColumns: doeOptimize === 'target' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>优化方向</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { key: 'maximize', label: '望大', icon: '\u2191' },
                  { key: 'minimize', label: '望小', icon: '\u2193' },
                  { key: 'target', label: '望目', icon: '\u25ce' },
                ].map(opt => (
                  <div key={opt.key} onClick={() => { setDoeOptimize(opt.key); if (opt.key !== 'target') setDoeTarget(null); }}
                    style={{
                      flex: 1, padding: '6px 4px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                      background: doeOptimize === opt.key ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                      color: doeOptimize === opt.key ? '#fff' : 'var(--text-muted)',
                      fontSize: 11, fontWeight: 600, transition: 'all 0.12s',
                    }}>
                    {opt.icon} {opt.label}
                  </div>
                ))}
              </div>
            </div>
            {doeOptimize === 'target' && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>目标值</div>
                <InputNumber value={doeTarget} onChange={v => setDoeTarget(v)} style={{ width: '100%' }} placeholder="Target" size="small" />
              </div>
            )}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, letterSpacing: 0.5 }}>响应变量 (Y)</div>
              <Select value={doeResponseCol || undefined} onChange={setDoeResponseCol} allowClear
                placeholder="自动检测" style={{ width: '100%' }} size="small"
                options={columns
                  .filter(c => ['float64','int64','float','integer','number'].includes(c.dtype))
                  .filter(c => !DOE_META_NAMES.has(c.name.toLowerCase().replace(/[_ ]/g, '')))
                  .map(c => ({ value: c.name, label: c.name }))}
              />
            </div>
          </div>

          {/* Factor tags */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5, letterSpacing: 0.5 }}>因子列 (X) — 留空自动检测</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {columns
                .filter(c => c.name !== doeResponseCol)
                .filter(c => !DOE_META_NAMES.has(c.name.toLowerCase().replace(/[_ ]/g, '')))
                .map(c => {
                  const sel = doeFactorCols.includes(c.name);
                  return (
                    <div key={c.name} onClick={() => setDoeFactorCols(prev => sel ? prev.filter(x => x !== c.name) : [...prev, c.name])}
                      style={{
                        padding: '3px 9px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 500,
                        background: sel ? '#533afd' : (isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5'),
                        color: sel ? '#fff' : 'var(--text-secondary)',
                        transition: 'all 0.12s',
                      }}>
                      {c.name} <span style={{ opacity: 0.6, fontSize: 9 }}>{c.unique}L</span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Action */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setDoeModalOpen(false)} style={{ borderRadius: 8 }}>取消</Button>
            <Button type="primary" style={{ borderRadius: 8, minWidth: 120 }}
              onClick={() => {
                if (doeOptimize === 'target' && doeTarget === null) { message.warning('望目优化需要填写目标值'); return; }
                setDoeModalOpen(false);
                for (const k of Array.from(cacheRef.current.keys())) { if (k.startsWith('doe_')) cacheRef.current.delete(k); }
                if (pendingModule) {
                  executeAnalysis(pendingModule);
                } else {
                  executeAnalysis('doe');
                }
              }}>
              开始分析
            </Button>
          </div>
        </div>
      </>)}
    </div>
  );
};

function buildStatsTable(statistics: any) {
  if (!statistics || typeof statistics !== 'object') return { columns: [], data: [] };
  const entries = Object.entries(statistics).filter(([_, v]) => typeof v === 'number' || typeof v === 'string');
  if (entries.length === 0) return { columns: [], data: [] };
  return {
    columns: [
      { title: '统计量', dataIndex: 'stat', key: 'stat' },
      { title: '值', dataIndex: 'value', key: 'value' },
    ],
    data: entries.map(([k, v], i) => ({
      key: String(i), stat: k,
      value: typeof v === 'number' ? (Number.isFinite(v) ? v.toFixed(4) : '0') : v,
    })),
  };
}

export default AnalysisWorkspace;
